import { useEffect, useRef, useState } from "react";
import ImageProps, { DrawingLayerData, DrawingStroke, ExtendPadding, ImageLayerProps, LayerAreaSelection, LayerAreaSelectionMode, LayerProps, OverlayRect, Point } from "../../../types/imageStudio/images";
import { clientToCanvasPoint, getKeyboardCornerFromCode } from "../../../utils/images/overlayFunctions";
import { applyFloodFill, applyLayerResizeContentSync, canvasDeltaToLayerLocalDelta, canvasPointToLayerLocalPoint, clampPointToLayer, cloneLayer, closeFreePath, cropImageLayerDataUrl, getHighestOrderNumber, getLayerBoundsUnion, getLayerDisplayStyle, getLayerPositionKeepingLocalPoint, getLayerRasterSource, getNextLayerMeta, getSmallestLayerId, getTopMostLayerAtPoint, getVisualPaddingAsLocalPadding, hexToRgbaCss, isPointInsideSelectionShape, layerLocalPointToCanvasPoint, mergeLayerAndGetLayerWithSelectionIds, normalizeRect, padImageSrcTransparent, padImageSrcTransparentToSize, rebuildOrderNumbers, rectsIntersect, rectToSelectedLayerCrop, renderLayerToCanvas, sortLayersByOrderAscending } from "../../../utils/images/layerFunctions";
import { canvasToFile, loadImage } from "../../../utils/images/imagesFunctions";
import { colorFilterList } from "../../../utils/images/colorFilter";
import { urlToFile } from "../../../utils/fileHandling";
import { AreaOperationResult, CanvasDisplayRectProps, DrawingCursorPreviewProps, DrawingStrokePreviewProps, LayerComponentProps, MarqueeSelectionPreviewProps, SelectionPreviewProps, ShapePreviewProps } from "../../../types/imageStudio/layerComponent";
import { LayerSidebar } from "./LayerSidebar";

export const useLayerFunctionAndSidebar = ({
    image,
    baseImageVariations,
    imageChangeSource = "external",
    canvasRef,
    canvasWrapperRef,
    canvasDisplayRect,
    cropMode,
    rotateMode,
    isRotating,
    rotationDraft=0,
    selectedLayer,
    selectedLayerIDs = [],
    activeToolkitTool = "pointer",
    toolkitAction = null,
    isClosingOverlays = false,
    overlayTarget="image",
    selectedDrawingColor = "#9381FF",
    selectedBrushSize = 10,
    selectedBrushHardness = 100,
    selectedBrushOpacity = 100,
    selectedDrawingTool = "",
    baseImage,
    selectedVariationIndex = null,
    openExtandImage,
    onVariationHoverBaseImage,
    onSelectedVariationChange,
    onCancelSelection,
    onSelectBaseImage,
    onSelectedLayerChange,
    onSelectedLayerIDsChange,
    onToggleCropeMode,
    onToggleRotationMode,
    onRotatePreviewChange,
    onChangeRotationDraft,
    onIsRotating,
    handleRotateMove,
    setIsClosingOverlays,
    onBaseImageReplace,
    resetCanvasView,
    clearSidebarItems,
    handleWriteLayerOnBaseImage,
    handleZoomIn,
    handleZoomOut,
    flipCanvasHorizontal,
    flipCanvasVertical,
    handleRedoBase,
    handleResetBase,
    handleUndoBase,
    onCropBaseImage,
    openRotate,
    onDeleteVariation
}:LayerComponentProps):[
        imageLayer: LayerProps[],
        onLayersChange: React.Dispatch<React.SetStateAction<LayerProps[]>>,
        overlayRect: OverlayRect,
        layerAreaSelection: LayerAreaSelection | null,
        marqueeSelectionPreview: MarqueeSelectionPreviewProps| null,
        selectionPreview: SelectionPreviewProps| null,
        activeOverlayBaseRect: CanvasDisplayRectProps | null,
        activeLayerEditId: number | null,
        handleCropSelectedLayerSave: () => Promise<void>,
        rotateSelectedLayer: (degrees: number, layerID?:number) => void,
        updatePointerSelection: (e: React.MouseEvent, layer: LayerProps) => void,
        beginLayerTransform: (e: React.MouseEvent,layer: LayerProps,mode: "move" | "nw" | "ne" | "sw" | "se") => void, 
        beginSelectionDrag: (e: React.MouseEvent, targetLayer?: LayerProps | null) => void,
        beginDrawingStroke: (e: React.MouseEvent) => void, 
        beginPointerMarqueeSelection: (e: React.MouseEvent) => void,
        setLayerAreaSelection: React.Dispatch<React.SetStateAction<LayerAreaSelection | null>>,
        setOverlayRect: React.Dispatch<React.SetStateAction<OverlayRect>>,
        deleteSelectedAreaFromLayer: () => Promise<AreaOperationResult | null>,
        copySelectedAreaFromLayer: () => Promise<AreaOperationResult | null>,
        buildAiReferenceLayerFromSelection: () => Promise<LayerProps | null>,
        buildAiReferenceLayerFromLayer: (layer: LayerProps) => Promise<LayerProps | null>,  
        extractSelectedAreaToNewLayer: () => Promise<AreaOperationResult | null>,
        duplicateSelectedLayer: () => void,
        drawingCursorPreview: DrawingCursorPreviewProps | null,
        drawingStrokePreview: DrawingStrokePreviewProps | null,
        shapePreview: ShapePreviewProps | null,
        toggleSelectedTextStyle: (styleKey: "bold" | "italic" | "underline", enabled: boolean) => void,
        setSelectedTextContent: (text: string) => void,
        setSelectedTextColor: (color: string) => void,
        setSelectedTextOpacity: (opacityPercent: number) => void,
        setSelectedTextSize: (fontSize: number) => void,
        setSelectedTextFontFamily: (fontFamily: string) => void,
        setSelectedTextBorderColor: (color: string) => void,
        setSelectedTextBorderOpacity: (opacityPercent: number) => void,
        setSelectedTextBorderWidth: (borderWidth: number) => void,
        setSelectedTextBorderStyle: (borderStyle: "none" | "solid" | "dashed" | "dotted") => void,
        setSelectedTextBackgroundColor: (color: string) => void,
        setSelectedTextBackgroundOpacity: (opacityPercent: number) => void,
        setSelectedImageLayerSource: (img: ImageProps) => void,
        setSelectedImageLayerOpacity: (opacityPercent: number) => void,
        setSelectedImageLayerBackgroundColor: (color: string) => void,
        setSelectedImageLayerBackgroundOpacity: (opacityPercent: number) => void,
        setSelectedImageLayerBorderColor: (color: string) => void,
        setSelectedImageLayerBorderOpacity: (opacityPercent: number) => void,
        setSelectedImageLayerBorderWidth: (borderWidth: number) => void,
        setSelectedImageLayerBorderStyle: (borderStyle: "none" | "solid" | "dashed" | "dotted") => void,
        createImageLayerFromImage: (img: ImageProps, x?: number, y?: number,options?: { height?: number }) => void,
        undoSelectedLayerContentChange: () => void,
        redoSelectedLayerContentChange: () => void,
        commitLayerChange: (nextLayersRaw: LayerProps[], nextSelectedIds: number[] | undefined) => void,
        extendSelectedLayerTransparent: (padding: ExtendPadding) => Promise<void>,
        handleLayerToBack: () => void,
        handleLayerToFront: () => void,
        layerSideBar: JSX.Element
    ] => {
    const [layerAreaSelection, setLayerAreaSelection] = useState<LayerAreaSelection | null>(null);
    const [overlayRect, setOverlayRect] = useState<OverlayRect>({ x: 0.1, y: 0.1, w: 0.8, h: 0.8 });
    const [activeLayerEditId, setActiveLayerEditId] = useState<number | null>(null);
    const [layers, setLayers] = useState<LayerProps[]>([]);
    const [baseImageReplaceByVariation, setBaseImageReplaceByVariation] = useState<boolean>(false);

    const selectedSingleLayer =
        activeLayerEditId != null
            ? layers.find((l) => l.layerID === activeLayerEditId) ?? null
            : selectedLayerIDs.length === 1
                ? layers.find((l) => l.layerID === selectedLayerIDs[0]) ?? null
                : null;

    const layerDisplayRect = selectedSingleLayer ? getLayerDisplayStyle(selectedSingleLayer, canvasRef, canvasDisplayRect) : null;
    const activeOverlayBaseRect =
        cropMode
            ? layerDisplayRect
            : canvasDisplayRect;

    const [marqueeSelectionPreview, setMarqueeSelectionPreview] = useState< MarqueeSelectionPreviewProps| null>(null);
    const layerDragRef = useRef<{
        primaryLayerId: number;
        selectedIds: number[];
        mode: "move" | "nw" | "ne" | "sw" | "se";
        startX: number;
        startY: number;
        startLayers: LayerProps[];
    } | null>(null);
    const marqueeSelectionRef = useRef<{
        startX: number;
        startY: number;
        currentX: number;
        currentY: number;
    } | null>(null);
    const drawingStrokeRef = useRef<{
        layerID: number;
        tool: string;
        color: string;
        size: number;
        opacity: number;
        hardness: number;
        points: { x: number; y: number }[];
        startPoint: { x: number; y: number };
        currentPoint: { x: number; y: number };
    } | null>(null);
    const [drawingCursorPreview, setDrawingCursorPreview] = useState<DrawingCursorPreviewProps | null>(null);
    const [shapePreview, setShapePreview] = useState<ShapePreviewProps | null>(null);
    const [drawingStrokePreview, setDrawingStrokePreview] = useState<DrawingStrokePreviewProps | null>(null);
    const isShapeTool = (tool: string) => tool.startsWith("shape-");
    const selectionDragRef = useRef<{
        mode: "rect" | "oval" | "free";
        startX: number;
        startY: number;
        currentX: number;
        currentY: number;
        points: { x: number; y: number }[];
    } | null>(null);
    const clipboardRef = useRef<
        | { type: "layers"; layers: LayerProps[] }
        | { type: "area" }
        | null
    >(null);
    const [selectionPreview, setSelectionPreview] = useState< SelectionPreviewProps| null>(null);

    //overlay handling
    const openCrop = (layerId: number) => {
        //avoid cropping if rotation or expanding is active
        if (rotateMode || openExtandImage?.active) return;
        onToggleRotationMode?.(false);
        setActiveLayerEditId(layerId ?? null);
        onToggleCropeMode?.(true);
        setOverlayRect({ x: 0.1, y: 0.1, w: 0.8, h: 0.8 });
    };

    const startRotate = (initial:number,layerId?: number) => {
        //avoid rotation if cropping or expanding is active
        if (cropMode || openExtandImage?.active) return;
        onToggleCropeMode?.(false);
        setActiveLayerEditId(layerId ?? null);
        onToggleRotationMode?.(true);
        onChangeRotationDraft?.(initial);
    };

    const stopRotate = () => {
        onIsRotating?.(false);
    };

    //layer functions
    const getSingleSelectedLayer = () => {
        if (selectedLayerIDs.length !== 1) return null;
        return layers.find((layer) => layer.layerID === selectedLayerIDs[0]) ?? null;
    };
    const getSingleSelectedDrawingLayer = () => {
        if (selectedLayerIDs.length !== 1) return null;
        const layer = layers.find((l) => l.layerID === selectedLayerIDs[0]);
        if (!layer || layer.type !== "drawings") return null;
        return layer;
    };
    const updateSelectedTextLayer = (
        updater: (textData: NonNullable<LayerProps["textData"]>) => NonNullable<LayerProps["textData"]>
    ) => {
        if (selectedLayerIDs.length !== 1) return;

        const selectedId = selectedLayerIDs[0];
        const targetLayer = layers.find((layer) => layer.layerID === selectedId);

        if (!targetLayer || targetLayer.type !== "text" || !targetLayer.textData) return;

        const nextLayers = layers.map((layer) => {
            if (layer.layerID !== selectedId || layer.type !== "text" || !layer.textData) {
                return layer;
            }

            return {
                ...layer,
                textData: updater(layer.textData)
            };
        });

        commitLayerContentChange(nextLayers, [selectedId], [selectedId]);
    };
    const updateSelectedImageLayer = (
        updater: (layer: LayerProps) => LayerProps
    ) => {
        if (selectedLayerIDs.length !== 1) return;

        const selectedId = selectedLayerIDs[0];
        const targetLayer = layers.find((layer) => layer.layerID === selectedId);

        if (!targetLayer || (targetLayer.type !== "image" && targetLayer.type !== "ai-variation")) return;

        const nextLayers = layers.map((layer) => {
            if (layer.layerID !== selectedId) return layer;
            return updater(layer);
        });

        commitLayerContentChange(nextLayers, [selectedId], [selectedId]);
    };
    const toggleSelectedTextStyle = (styleKey: "bold" | "italic" | "underline", enabled: boolean) => {
        updateSelectedTextLayer((textData) => {
            const currentStyles = textData.style ?? [];
            const nextStyles = enabled
                ? Array.from(new Set([...currentStyles, styleKey]))
                : currentStyles.filter((item) => item !== styleKey);

            return {
                ...textData,
                style: nextStyles
            };
        });
    };

    const setSelectedTextColor = (color: string) => {
        updateSelectedTextLayer((textData) => ({
            ...textData,
            fontColor: color
        }));
    };
    const setSelectedTextOpacity = (opacityPercent: number) => {
        updateSelectedTextLayer((textData) => ({
            ...textData,
            textOpacity: Math.max(0, Math.min(1, opacityPercent / 100))
        }));
    };
    const setSelectedTextSize = (fontSize: number) => {
        updateSelectedTextLayer((textData) => ({
            ...textData,
            fontSize: `${Math.max(1, fontSize)}px`,
            autoFit: false
        }));
    };
    const setSelectedTextFontFamily = (fontFamily: string) => {
        updateSelectedTextLayer((textData) => ({
            ...textData,
            fontFamily
        }));
    };
    const setSelectedTextBorderColor = (color: string) => {
        updateSelectedTextLayer((textData) => ({
            ...textData,
            borderColor: color
        }));
    };
    const setSelectedTextBorderOpacity = (opacityPercent: number) => {
        updateSelectedTextLayer((textData) => ({
            ...textData,
            borderOpacity: Math.max(0, Math.min(1, opacityPercent / 100))
        }));
    };
    const setSelectedTextBorderWidth = (borderWidth: number) => {
        updateSelectedTextLayer((textData) => ({
            ...textData,
            borderWidth: Math.max(0, borderWidth)
        }));
    };
    const setSelectedTextBorderStyle = (borderStyle: "none" | "solid" | "dashed" | "dotted") => {
        updateSelectedTextLayer((textData) => ({
            ...textData,
            borderStyle
        }));
    };
    const setSelectedTextBackgroundColor = (color: string) => {
        updateSelectedTextLayer((textData) => ({
            ...textData,
            backgroundColor: color
        }));
    };
    const setSelectedTextBackgroundOpacity = (opacityPercent: number) => {
        updateSelectedTextLayer((textData) => ({
            ...textData,
            backgroundOpacity: Math.max(0, Math.min(1, opacityPercent / 100))
        }));
    };
    const setSelectedTextContent = (text: string) => {
        updateSelectedTextLayer((textData) => ({
            ...textData,
            text,
            autoFit: false
        }));
    };

    const toImageLayerProps = (
        img: ImageProps,
        previous?: ImageLayerProps | null
    ): ImageLayerProps => ({
        file: img.file,
        src: img.src,
        width: img.width,
        height: img.height,
        timeOfUse: img.timeOfUse,
        title: img.title,
        alt: img.alt,
        onClick: img.onClick,
        onHover: img.onHover,

        rotation: previous?.rotation ?? 0,
        prompt_positive: previous?.prompt_positive ?? "",
        prompt_negative: previous?.prompt_negative ?? "",
        colorFilterData: previous?.colorFilterData ?? null
    });

    const setLayerHiddenState = (layerID: number, hidden: boolean) => {
        setLayers((prevLayers) =>
            prevLayers.map((layer) =>
                layer.layerID === layerID
                    ? { ...layer, hidden }
                    : layer
            )
        );
        //unselect layer if state was set to hidden
        if (hidden) {
            const nextSelectedIds = selectedLayerIDs.filter((id) => id !== layerID);

            onSelectedLayerIDsChange?.(nextSelectedIds);

            if (nextSelectedIds.length === 1) {
                const nextSelectedLayer =
                    layers.find((layer) => layer.layerID === nextSelectedIds[0] && !layer.hidden) ?? null;

                onSelectedLayerChange?.(nextSelectedLayer);
            } else {
                onSelectedLayerChange?.(null);
            }
        }
    };

    const previewSelectedLayerVariation = (img: ImageProps | null) => {
        if (!selectedLayer) return;

        const selectedId = selectedLayer.layerID;
        const newLayers = layers.map((layer) => {
                if (layer.layerID !== selectedId) return layer;

                if (!img) {
                    if(layer.type === "drawings" || layer.type === "text"){
                        return {
                            ...layer,
                            imageData: undefined
                        }
                    }
                    else{
                        return {
                            ...layer,
                            imageEdit: layer.imageData ?? undefined
                        }
                    }
                } 

                const previewImage = toImageLayerProps(
                    img,
                    layer.imageEdit ?? layer.imageData ?? null
                );

                if(layer.type === "drawings" || layer.type === "text"){
                        return {
                            ...layer,
                            imageData: previewImage
                        }
                    }
                else {
                    return {
                        ...layer,
                        imageEdit: previewImage
                    };
                }
            })

        setLayers(newLayers);
    };
    
    const getSelectedVariationImage = (): ImageProps | null => {
        if (!selectedLayer) return null;
        if (selectedVariationIndex == null || selectedVariationIndex < 0) return null;

        const variations = selectedLayer.variations ?? [];
        return variations[selectedVariationIndex] ?? null;
    };
    const handleVariationHoverInternal = (index: number, img: ImageProps | null) => {
        const hasSelectedVariation =
            selectedVariationIndex !== null &&
            selectedVariationIndex >= 0;

        if (!selectedLayer && selectedLayerIDs.length < 1) {
            if (!hasSelectedVariation) {
                onVariationHoverBaseImage?.(img);
            }
            return;
        }

        if (hasSelectedVariation) {
            const selectedVariation = getSelectedVariationImage();
            previewSelectedLayerVariation(selectedVariation);
            return;
        }

        previewSelectedLayerVariation(img);
    };
    const handleOnCancelSelectionInternal = () => {
        previewSelectedLayerVariation(null);
        onSelectedVariationChange?.(-1,null);
        onCancelSelection?.();
    };
    const handleSelectedVariationChangeInternal = (index: number, img: ImageProps | null) => {
        if(!selectedLayer && selectedLayerIDs.length < 1){
            
            //hover base image
            // onVariationHoverBaseImage?.(img);
            onSelectedVariationChange?.(index, img);
            return;
        }
        previewSelectedLayerVariation(img);
        onSelectedVariationChange?.(index, img);
    };
    const setSelectedImageLayerSource = (img: ImageProps) => {
        updateSelectedImageLayer((layer) => {
            const nextImageData = toImageLayerProps(img, layer.imageData ?? null);
            const nextImageEdit = toImageLayerProps(img, layer.imageEdit ?? layer.imageData ?? null);

            return {
                ...layer,
                width: layer.width,
                height: layer.height,
                imageData: nextImageData,
                imageEdit: nextImageEdit
            };
        });
    };
    const clearVariationPreviewFromLayers = (
        sourceLayers: LayerProps[],
        targetLayerId: number
    ): LayerProps[] => {
        return sourceLayers.map((layer) => {
            if (layer.layerID !== targetLayerId) return layer;

            if (layer.type === "drawings" || layer.type === "text") {
                return {
                    ...layer,
                    imageData: undefined
                };
            }

            return {
                ...layer,
                imageEdit: layer.imageData ?? undefined
            };
        });
    };
    const replaceSelectedLayerWithVariation = (img: ImageProps, targetLayer: LayerProps | null) => {
        if (!targetLayer) return;

        const nextImageData = toImageLayerProps(img, targetLayer.imageData ?? null);
        const nextImageEdit = toImageLayerProps(img, targetLayer.imageEdit ?? targetLayer.imageData ?? null);

        const nextLayers: LayerProps[] = layers.map((layer) => {
            if (layer.layerID !== targetLayer.layerID) return layer;

            return {
                ...layer,
                type: "image",
                textData: undefined,
                drawingData: undefined,
                imageData: nextImageData,
                imageEdit: nextImageEdit,
                imageLayerStyle: layer.imageLayerStyle ?? {
                    imageOpacity: 1,
                    backgroundColor: "transparent",
                    backgroundOpacity: 1,
                    borderColor: "#ffffff",
                    borderOpacity: 1,
                    borderWidth: 0,
                    borderStyle: "none"
                }
            };
        });
        commitLayerContentChange(nextLayers, [targetLayer.layerID], [targetLayer.layerID]);
        onSelectedVariationChange?.(-1, null);
        onCancelSelection?.();
    };
    const handleBaseImageReplaceWithVariationInternal = (img:ImageProps) => {
        setBaseImageReplaceByVariation(true);
        onBaseImageReplace?.(img);
        onSelectedVariationChange?.(-1,null);
        resetCanvasView();
    }
    const addSelectionAsNewImageLayerVariation = (img: ImageProps, targetLayer: LayerProps | null) => {
        let newLayer = createLayerBase('image');
        const nextImageData = toImageLayerProps(img, targetLayer?.imageData ?? null);
        const nextImageEdit = toImageLayerProps(img, targetLayer?.imageEdit ?? targetLayer?.imageData ?? null);
        newLayer.imageData = nextImageData;
        newLayer.imageEdit = nextImageEdit;
        newLayer.type = 'ai-variation';
        newLayer.height = targetLayer?.height ?? image?.height ?? newLayer.height;
        newLayer.width = targetLayer?.width ?? image?.width ?? newLayer.width;
        newLayer.position = targetLayer?.position ?? newLayer.position;
        newLayer.imageLayerStyle = targetLayer?.imageLayerStyle 
            ?? {
                imageOpacity: 1,
                backgroundColor: "transparent",
                backgroundOpacity: 1,
                borderColor: "#ffffff",
                borderOpacity: 1,
                borderWidth: 0,
                borderStyle: "none"
            };
        const layersWithoutPreview = targetLayer
            ? clearVariationPreviewFromLayers(layers, targetLayer.layerID)
            : layers;

        commitLayerChange([...layersWithoutPreview, newLayer], [newLayer.layerID]);

        onSelectedVariationChange?.(-1, null);
        onCancelSelection?.();

    };
    const addVariationToBaseImage = async (img: ImageProps, targetLayer: LayerProps | null) => {
        if (!image?.src || !targetLayer || !onBaseImageReplace) return;
        //avoid removing layers
        setBaseImageReplaceByVariation(true);

        const base = await loadImage(image.src);
        const overlay = await loadImage(img.src);

        const canvas = document.createElement("canvas");
        canvas.width = base.width;
        canvas.height = base.height;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(base, 0, 0, canvas.width, canvas.height);

        ctx.drawImage(
            overlay,
            targetLayer.position.x,
            targetLayer.position.y,
            targetLayer.width,
            targetLayer.height
        );

        // eslint-disable-next-line no-restricted-syntax -- internes Layer-Compositing-State (kein Download), Art. 50 KI-VO nicht einschlaegig
        const src = canvas.toDataURL("image/png");
        //setting ref avoidds removing current layer
        lastExternalImageSrcRef.current = src;
        const file = await canvasToFile(canvas, "base-image-with-variation");
        //set layer on image
        const baseImageData: ImageProps =  {
            file: file,
            src: src,
            width: canvas.width,
            height: canvas.height,
            timeOfUse: new Date()
        };
        onBaseImageReplace(baseImageData);
        //remove selected Layer
        onSelectedLayerChange?.(null);
        onSelectedLayerIDsChange?.([]);
        onSelectedVariationChange?.(-1,null);
        const newLayers:LayerProps[] = [...layers.filter(item => item.layerID !== targetLayer.layerID)];
        commitLayerChange(newLayers,[]);
    };

    const setSelectedImageLayerOpacity = (opacityPercent: number) => {
        updateSelectedImageLayer((layer) => ({
            ...layer,
            imageLayerStyle: {
                ...layer.imageLayerStyle,
                imageOpacity: Math.max(0, Math.min(1, opacityPercent / 100))
            }
        }));
    };
    const setSelectedImageLayerBackgroundColor = (color: string) => {
        updateSelectedImageLayer((layer) => ({
            ...layer,
            imageLayerStyle: {
                ...layer.imageLayerStyle,
                backgroundColor: color
            }
        }));
    };
    const setSelectedImageLayerBackgroundOpacity = (opacityPercent: number) => {
        updateSelectedImageLayer((layer) => ({
            ...layer,
            imageLayerStyle: {
                ...layer.imageLayerStyle,
                backgroundOpacity: Math.max(0, Math.min(1, opacityPercent / 100))
            }
        }));
    };
    const setSelectedImageLayerBorderColor = (color: string) => {
        updateSelectedImageLayer((layer) => ({
            ...layer,
            imageLayerStyle: {
                ...layer.imageLayerStyle,
                borderColor: color
            }
        }));
    };
    const setSelectedImageLayerBorderOpacity = (opacityPercent: number) => {
        updateSelectedImageLayer((layer) => ({
            ...layer,
            imageLayerStyle: {
                ...layer.imageLayerStyle,
                borderOpacity: Math.max(0, Math.min(1, opacityPercent / 100))
            }
        }));
    };
    const setSelectedImageLayerBorderWidth = (borderWidth: number) => {
        updateSelectedImageLayer((layer) => ({
            ...layer,
            imageLayerStyle: {
                ...layer.imageLayerStyle,
                borderWidth: Math.max(0, borderWidth)
            }
        }));
    };
    const setSelectedImageLayerBorderStyle = (borderStyle: "none" | "solid" | "dashed" | "dotted") => {
        updateSelectedImageLayer((layer) => ({
            ...layer,
            imageLayerStyle: {
                ...layer.imageLayerStyle,
                borderStyle
            }
        }));
    };
    const extendSelectedLayerTransparent = async (padding: ExtendPadding) => {
        const selectedId = selectedLayerIDs.length === 1 ? selectedLayerIDs[0] : null;
        if (selectedId == null) return;

        const nextLayers = await Promise.all(
            layers.map(async layer => {
                if (layer.layerID !== selectedId) return layer;

                const localPadding = getVisualPaddingAsLocalPadding(layer, padding);

                const nextWidth = layer.width + localPadding.left + localPadding.right;
                const nextHeight = layer.height + localPadding.top + localPadding.bottom;

                const oldContentCenterCanvas = layerLocalPointToCanvasPoint(
                    {
                        x: layer.width / 2,
                        y: layer.height / 2
                    },
                    layer
                );

                const nextLayer: LayerProps = {
                    ...layer,
                    width: nextWidth,
                    height: nextHeight,
                    position: getLayerPositionKeepingLocalPoint(
                        layer,
                        nextWidth,
                        nextHeight,
                        {
                            x: localPadding.left + layer.width / 2,
                            y: localPadding.top + layer.height / 2
                        },
                        oldContentCenterCanvas
                    )
                };

                if ((layer.type === "image" || layer.type === "ai-variation") && (layer.imageEdit?.src || layer.imageData?.src)) {
                    const src = layer.imageEdit?.src ?? layer.imageData!.src;
                    const padded = await padImageSrcTransparentToSize(
                        src,
                        localPadding,
                        layer.width,
                        layer.height,
                        layer.imageEdit?.file?.name ?? "extended-layer.png"
                    );

                    nextLayer.imageData = {
                        ...layer.imageData,
                        file: padded.file,
                        src: padded.src,
                        width: padded.width,
                        height: padded.height,
                        timeOfUse: new Date()
                    } as ImageLayerProps;

                    nextLayer.imageEdit = {
                        ...layer.imageEdit,
                        file: padded.file,
                        src: padded.src,
                        width: padded.width,
                        height: padded.height,
                        timeOfUse: new Date()
                    } as ImageLayerProps;
                }

                if (layer.type === "drawings") {
                    const paddedSrc = layer.drawingData?.rasterSrc
                        ? (await padImageSrcTransparent(
                            layer.drawingData.rasterSrc,
                            localPadding,
                            "extended-drawing.png"
                        )).src
                        : createTransparentRaster(nextWidth, nextHeight);

                    const nextDrawingData: DrawingLayerData = {
                        strokes: layer.drawingData?.strokes ?? [],
                        rasterSrc: paddedSrc,
                        rasterWidth: nextWidth,
                        rasterHeight: nextHeight,
                        rotation: layer.drawingData?.rotation ?? 0,
                        flipHorizontal: layer.drawingData?.flipHorizontal ?? false,
                        flipVertical: layer.drawingData?.flipVertical ?? false
                    };

                    nextLayer.drawingData = nextDrawingData;
                }

                return nextLayer;
            })
        );

        commitLayerContentChange(nextLayers, [selectedId], [selectedId]);
    };
    const createImageLayerFromImage = (img: ImageProps, x?: number, y?: number,options?: { height?: number }) => {
        const { nextLayerID, nextOrderNumber } = getNextLayerMeta(layers);

        const canvas = canvasRef?.current as HTMLCanvasElement | null;
        const canvasWidth = canvas?.width ?? 1000;
        const canvasHeight = canvas?.height ?? 800;

        let width: number;
        let height: number;

        if (options?.height) {
            height = Math.max(1, Math.round(options.height));
            width = Math.max(
                1,
                Math.round((img.width / Math.max(1, img.height)) * height)
            );
        } else {
            const maxWidth = Math.max(1, canvasWidth * 0.10);
            const maxHeight = Math.max(1, canvasHeight * 0.10);

            const scale = Math.min(
                1,
                maxWidth / Math.max(1, img.width),
                maxHeight / Math.max(1, img.height)
            );

            width = Math.max(1, Math.round(img.width * scale));
            height = Math.max(1, Math.round(img.height * scale));
        }

        const imageLayerData = toImageLayerProps(img);

        const newLayer: LayerProps = {
            type: "image",
            layerID: nextLayerID,
            orderNumber: nextOrderNumber,
            width,
            height,
            hidden: false,
            position: {
                x: typeof x === "number" ? Math.round(x - width / 2) : 40,
                y: typeof y === "number" ? Math.round(y - height / 2) : 40
            },
            imageData: imageLayerData,
            imageEdit: { ...imageLayerData },
            imageLayerStyle: {
                imageOpacity: 1,
                backgroundColor: "transparent",
                backgroundOpacity: 1,
                borderColor: "#ffffff",
                borderOpacity: 1,
                borderWidth: 0,
                borderStyle: "none"
            }
        };

        const nextLayers = [...layers, newLayer];
        commitLayerChange(nextLayers, [newLayer.layerID]);
    };

    const clampPointToTransformedLayer = (point: Point, layer: LayerProps): Point => {
        const local = canvasPointToLayerLocalPoint(point, layer);

        const clampedLocal = {
            x: Math.max(0, Math.min(layer.width, local.x)),
            y: Math.max(0, Math.min(layer.height, local.y))
        };

        return layerLocalPointToCanvasPoint(clampedLocal, layer);
    };

    const getLocalSelectionRectForLayer = (
        selection: LayerAreaSelection,
        layer: LayerProps
    ) => {
        const sourcePoints =
            selection.points && selection.points.length > 0
                ? selection.points
                : [
                    { x: selection.x, y: selection.y },
                    { x: selection.x + selection.w, y: selection.y },
                    { x: selection.x + selection.w, y: selection.y + selection.h },
                    { x: selection.x, y: selection.y + selection.h }
                ];

        const localPoints = sourcePoints.map((p) =>
            canvasPointToLayerLocalPoint(p, layer)
        );

        const xs = localPoints.map((p) => p.x);
        const ys = localPoints.map((p) => p.y);

        const minX = Math.max(0, Math.min(...xs));
        const minY = Math.max(0, Math.min(...ys));
        const maxX = Math.min(layer.width, Math.max(...xs));
        const maxY = Math.min(layer.height, Math.max(...ys));

        return {
            x: minX,
            y: minY,
            w: Math.max(1, maxX - minX),
            h: Math.max(1, maxY - minY)
        };
    };  
    const getCanvasSelectionRect = (selection: LayerAreaSelection) => {
        return {
            x: Math.max(0, Math.round(selection.x)),
            y: Math.max(0, Math.round(selection.y)),
            w: Math.max(1, Math.round(selection.w)),
            h: Math.max(1, Math.round(selection.h))
        };
    };

    const getLocalSelectionPointsForLayer = (
        selection: LayerAreaSelection,
        layer: LayerProps
    ) => {
        const sourcePoints =
            selection.points && selection.points.length > 0
                ? selection.points
                : buildSelectionBoundaryPoints(
                    selection.mode,
                    selection.x,
                    selection.y,
                    selection.w,
                    selection.h
                );

        return sourcePoints.map((p) => canvasPointToLayerLocalPoint(p, layer));
    };

    const drawPolygonPath = (
        ctx: CanvasRenderingContext2D,
        points: Point[]
    ) => {
        if (!points.length) return;

        ctx.beginPath();
        points.forEach((p, index) => {
            if (index === 0) ctx.moveTo(p.x, p.y);
            else ctx.lineTo(p.x, p.y);
        });
        ctx.closePath();
    };

    const renderSingleLayerToCanvasSpace = async (layer: LayerProps) => {
        const canvas = canvasRef?.current as HTMLCanvasElement | null;
        if (!canvas) return null;

        const outCanvas = document.createElement("canvas");
        outCanvas.width = canvas.width;
        outCanvas.height = canvas.height;

        const ctx = outCanvas.getContext("2d");
        if (!ctx) return null;

        await renderLayerToCanvas(ctx, layer, 0, 0);
        return outCanvas;
    };

    const buildSelectionBoundaryPoints = (
        mode: "rect" | "oval" | "free",
        x: number,
        y: number,
        w: number,
        h: number,
        freePoints: Point[] = []
    ): Point[] => {
        if (mode === "free") {
            return closeFreePath(freePoints);
        }

        if (mode === "rect") {
            return [
                { x, y },
                { x: x + w, y },
                { x: x + w, y: y + h },
                { x, y: y + h }
            ];
        }

        const points: Point[] = [];
        const cx = x + w / 2;
        const cy = y + h / 2;
        const rx = w / 2;
        const ry = h / 2;
        const steps = 32;

        for (let i = 0; i < steps; i++) {
            const angle = (i / steps) * Math.PI * 2;
            points.push({
                x: cx + Math.cos(angle) * rx,
                y: cy + Math.sin(angle) * ry
            });
        }

        return closeFreePath(points);
    };
    const getPolygonPoints = (
        cx: number,
        cy: number,
        radius: number,
        sides: number,
        rotationOffset = -Math.PI / 2
    ): Point[] => {
        return Array.from({ length: sides }).map((_, index) => {
            const angle = rotationOffset + (index / sides) * Math.PI * 2;
            return {
                x: cx + Math.cos(angle) * radius,
                y: cy + Math.sin(angle) * radius
            };
        });
    };

    const getShapePointsFromDrag = (
        tool: string,
        startPoint: Point,
        endPoint: Point
    ): Point[] => {
        const rect = normalizeRect(startPoint.x, startPoint.y, endPoint.x, endPoint.y);
        const cx = rect.x + rect.w / 2;
        const cy = rect.y + rect.h / 2;
        const radius = Math.min(rect.w, rect.h) / 2;

        switch (tool) {
            case "shape-circle": {
                const steps = 40;
                return Array.from({ length: steps }).map((_, index) => {
                    const angle = (index / steps) * Math.PI * 2;
                    return {
                        x: cx + Math.cos(angle) * rect.w / 2,
                        y: cy + Math.sin(angle) * rect.h / 2
                    };
                });
            }
            case "shape-line":
                return [
                    { x: startPoint.x, y: startPoint.y },
                    { x: endPoint.x, y: endPoint.y }
                ];
            case "shape-triangle":
                return getPolygonPoints(cx, cy, radius, 3);
            case "shape-square":
                return [
                    { x: rect.x, y: rect.y },
                    { x: rect.x + rect.w, y: rect.y },
                    { x: rect.x + rect.w, y: rect.y + rect.h },
                    { x: rect.x, y: rect.y + rect.h }
                ];
            case "shape-pentagon":
                return getPolygonPoints(cx, cy, radius, 5);
            case "shape-hexagon":
                return getPolygonPoints(cx, cy, radius, 6);
            case "shape-octagon":
                return getPolygonPoints(cx, cy, radius, 8);
            case "shape-diamond":
                return [
                    { x: rect.x + rect.w * 0.15, y: rect.y + rect.h * 0.35 }, // links außen
                    { x: rect.x + rect.w * 0.35, y: rect.y },                 // oben links
                    { x: rect.x + rect.w * 0.65, y: rect.y },                 // oben rechts
                    { x: rect.x + rect.w * 0.85, y: rect.y + rect.h * 0.35 }, // rechts außen
                    { x: rect.x + rect.w * 0.5, y: rect.y + rect.h }          // unten
                ];
            case "shape-rhombus":
                return [
                    { x: cx, y: rect.y },
                    { x: rect.x + rect.w, y: cy },
                    { x: cx, y: rect.y + rect.h },
                    { x: rect.x, y: cy }
                ];
            case "shape-trapeze":
                return [
                    { x: rect.x + rect.w * 0.2, y: rect.y },
                    { x: rect.x + rect.w * 0.8, y: rect.y },
                    { x: rect.x + rect.w, y: rect.y + rect.h },
                    { x: rect.x, y: rect.y + rect.h }
                ];
            default:
                return [];
        }
    };

    const closeShapePoints = (points: Point[]) => {
        if (points.length < 2) return points;

        const first = points[0];
        const last = points[points.length - 1];

        if (first.x === last.x && first.y === last.y) return points;

        return [...points, { ...first }];
    };
    const getPreviewShapePoints = (
        tool: string,
        startPoint: Point,
        currentPoint: Point
    ) => {
        const points = getShapePointsFromDrag(tool, startPoint, currentPoint);

        return tool === "shape-line"
            ? points
            : closeShapePoints(points);
    };
    const createTransparentRaster = (width: number, height: number) => {
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(width));
        canvas.height = Math.max(1, Math.round(height));
        // eslint-disable-next-line no-restricted-syntax -- erzeugt einen leeren internen Platzhalter-Raster (kein Download), Art. 50 KI-VO nicht einschlaegig
        return canvas.toDataURL("image/png");
    };

    const loadDrawingLayerCanvas = async (layer: LayerProps): Promise<HTMLCanvasElement> => {
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(layer.width));
        canvas.height = Math.max(1, Math.round(layer.height));

        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) return canvas;

        const src = layer.drawingData?.rasterSrc;

        if (src) {
            const img = await loadImage(src);
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        }

        return canvas;
    };

    const drawRasterStroke = (
        ctx: CanvasRenderingContext2D,
        stroke: DrawingStroke
    ) => {
        if (!stroke.points.length) return;

        const opacity = Math.max(0, Math.min(1, stroke.opacity ?? 1));
        const composite: GlobalCompositeOperation =
            stroke.tool === "eraser" ? "destination-out" : "source-over";

        const temp = document.createElement("canvas");
        temp.width = ctx.canvas.width;
        temp.height = ctx.canvas.height;

        const tctx = temp.getContext("2d");
        if (!tctx) return;

        tctx.lineCap = "round";
        tctx.lineJoin = "round";
        tctx.lineWidth = stroke.size;
        tctx.strokeStyle = stroke.color;

        tctx.beginPath();
        tctx.moveTo(stroke.points[0].x, stroke.points[0].y);

        for (let i = 1; i < stroke.points.length; i++) {
            tctx.lineTo(stroke.points[i].x, stroke.points[i].y);
        }

        tctx.stroke();

        ctx.save();
        ctx.globalCompositeOperation = composite;
        ctx.globalAlpha = opacity;
        ctx.drawImage(temp, 0, 0);
        ctx.restore();
    };
    const commitDrawingRaster = async (
        layerID: number,
        edit: (ctx: CanvasRenderingContext2D, layer: LayerProps) => Promise<void> | void
    ) => {
        const nextLayers = await Promise.all(
            layers.map(async (layer) => {
                if (layer.layerID !== layerID || layer.type !== "drawings") return layer;

                const canvas = await loadDrawingLayerCanvas(layer);
                const ctx = canvas.getContext("2d", { willReadFrequently: true });
                if (!ctx) return layer;

                await edit(ctx, layer);

                return {
                    ...layer,
                    drawingData: {
                        ...layer.drawingData,
                        strokes: [],
                        // eslint-disable-next-line no-restricted-syntax -- internes Layer-Compositing-State (kein Download), Art. 50 KI-VO nicht einschlaegig
                        rasterSrc: canvas.toDataURL("image/png"),
                        rasterWidth: canvas.width,
                        rasterHeight: canvas.height
                    }
                };
            })
        );

        commitLayerContentChange(nextLayers, [layerID], [layerID]);
    };
    const addDrawingStrokeToLayer = async (
        layerID: number,
        stroke: DrawingStroke
    ) => {
        await commitDrawingRaster(layerID, (ctx) => {
            if (stroke.tool === "fill") {
                const fillPoint = stroke.fillStart ?? stroke.points?.[0];
                if (!fillPoint) return;

                applyFloodFill(
                    ctx,
                    Math.round(fillPoint.x),
                    Math.round(fillPoint.y),
                    stroke.color,
                    stroke.opacity ?? 1
                );

                return;
            }

            drawRasterStroke(ctx, stroke);
        });
    };
    const stripLayerHistory = (layer: LayerProps): Omit<LayerProps, "history"> => {
        const { history, ...snapshot } = layer;
        return cloneLayer(snapshot as LayerProps);
    };

    const pushLayerHistory = (
        previousLayer: LayerProps,
        nextLayer: LayerProps
    ): LayerProps => {
        const previousSnapshot = stripLayerHistory(previousLayer);

        return {
            ...nextLayer,
            history: {
                past: [
                    ...(previousLayer.history?.past ?? []),
                    previousSnapshot
                ].slice(-20),
                future: []
            }
        };
    };

    const commitLayerContentChange = (
        nextLayersRaw: LayerProps[],
        changedLayerIds: number[],
        nextSelectedIds: number[] | undefined = selectedLayerIDs
    ) => {
        const nextLayersWithHistory = nextLayersRaw.map((nextLayer) => {
            if (!changedLayerIds.includes(nextLayer.layerID)) return nextLayer;

            const previousLayer = layers.find(
                (layer) => layer.layerID === nextLayer.layerID
            );

            if (!previousLayer) return nextLayer;

            return pushLayerHistory(previousLayer, nextLayer);
        });

        commitLayerChange(nextLayersWithHistory, nextSelectedIds);
    };

    const commitLayerChange = (
        nextLayersRaw: LayerProps[],
        nextSelectedIds: number[] | undefined = selectedLayerIDs,
    ) => {
        const nextLayers = rebuildOrderNumbers(nextLayersRaw);

        const normalizedSelectedIds = Array.isArray(nextSelectedIds)
            ? nextSelectedIds
            : selectedLayerIDs;

        //avoid changing hidden layer
        const validSelectedIds = normalizedSelectedIds.filter((id) =>
            nextLayers.some((layer) => layer.layerID === id && !layer.hidden)
        );

        const nextSelectedLayer =
            validSelectedIds.length === 1
                ? nextLayers.find((layer) => layer.layerID === validSelectedIds[0] && !layer.hidden) ?? null
                : null;
        setLayers(nextLayers);
        onSelectedLayerIDsChange?.(validSelectedIds);
        onSelectedLayerChange?.(nextSelectedLayer);
    };
    const undoSelectedLayerContentChange = () => {
        if (selectedLayerIDs.length !== 1) return;

        const selectedId = selectedLayerIDs[0];
        const currentLayer = layers.find((layer) => layer.layerID === selectedId);
        if (!currentLayer) return;

        const past = currentLayer.history?.past ?? [];
        if (past.length === 0) return;

        const previousSnapshot = past[past.length - 1];
        const nextPast = past.slice(0, -1);

        const currentSnapshot = stripLayerHistory(currentLayer);

        const restoredLayer: LayerProps = {
            ...previousSnapshot,
            history: {
                past: nextPast,
                future: [
                    currentSnapshot,
                    ...(currentLayer.history?.future ?? [])
                ].slice(0, 20)
            }
        };

        const nextLayers = layers.map((layer) =>
            layer.layerID === selectedId ? restoredLayer : layer
        );

        commitLayerChange(nextLayers, [selectedId]);
    };

    const redoSelectedLayerContentChange = () => {
        if (selectedLayerIDs.length !== 1) return;

        const selectedId = selectedLayerIDs[0];
        const currentLayer = layers.find((layer) => layer.layerID === selectedId);
        if (!currentLayer) return;

        const future = currentLayer.history?.future ?? [];
        if (future.length === 0) return;

        const nextSnapshot = future[0];
        const nextFuture = future.slice(1);

        const currentSnapshot = stripLayerHistory(currentLayer);

        const restoredLayer: LayerProps = {
            ...nextSnapshot,
            history: {
                past: [
                    ...(currentLayer.history?.past ?? []),
                    currentSnapshot
                ].slice(-20),
                future: nextFuture
            }
        };

        const nextLayers = layers.map((layer) =>
            layer.layerID === selectedId ? restoredLayer : layer
        );

        commitLayerChange(nextLayers, [selectedId]);
    };
    const handleSidebarReorder = (nextLayers: LayerProps[]) => {
        commitLayerChange(nextLayers, selectedLayerIDs);
    };
    const handleLayerToBack = () => {
        const currentLayer: LayerProps | null = selectedLayer ? {...selectedLayer} : null;
        if(!currentLayer) return;
        let currentAllLayers: LayerProps[] = [...layers];
        const currentIndex = currentAllLayers.findIndex(item => item.layerID === currentLayer.layerID); 
        currentAllLayers.splice(currentIndex,1)
        currentAllLayers = [currentLayer,...currentAllLayers];
        const reordered = currentAllLayers.map((layer, index) => ({
            ...layer,
            orderNumber: String(index + 1)
        }));

        handleSidebarReorder(reordered);
    };
    const handleLayerToFront= () => {
        const currentLayer: LayerProps | null = selectedLayer ? {...selectedLayer} : null;
        if(!currentLayer) return;
        let currentAllLayers: LayerProps[] = [...layers];
        const currentIndex = currentAllLayers.findIndex(item => item.layerID === currentLayer.layerID); 
        currentAllLayers.splice(currentIndex,1)
        currentAllLayers = [...currentAllLayers,currentLayer];
        const reordered = currentAllLayers.map((layer, index) => ({
            ...layer,
            orderNumber: String(index + 1)
        }));
        handleSidebarReorder(reordered);
    };

    const updatePointerSelection = (e: React.MouseEvent, layer: LayerProps) => {
        if (e.button !== 0) return;
        if (layer.hidden) return;
        if (cropMode || rotateMode || openExtandImage?.active) return;

        const isMultiToggle = e.ctrlKey || e.metaKey;
        const alreadySelected = selectedLayerIDs.includes(layer.layerID);

        const visibleSelectedIds = selectedLayerIDs.filter((id) =>
            layers.some((l) => l.layerID === id && !l.hidden)
        );

        if (isMultiToggle) {
            const nextIds = alreadySelected
                ? visibleSelectedIds.filter((id) => id !== layer.layerID)
                : [...visibleSelectedIds, layer.layerID];

            onSelectedLayerIDsChange?.(nextIds);

            if (nextIds.length === 1) {
                const onlyLayer = layers.find((l) => l.layerID === nextIds[0] && !l.hidden) ?? null;
                onSelectedLayerChange?.(onlyLayer);
            } else {
                onSelectedLayerChange?.(null);
            }

            return;
        }

        onSelectedLayerIDsChange?.([layer.layerID]);
        onSelectedLayerChange?.(layer);
    };
    const createLayerBase = (type: LayerProps["type"]): LayerProps => {
        const canvas = canvasRef?.current as HTMLCanvasElement | null;
        const canvasWidth = canvas?.width ??  1000;
        const canvasHeight = canvas?.height ?? 800;

        const width = 100;
        const height = 50;

        const { nextLayerID, nextOrderNumber } = getNextLayerMeta(layers);

        const baseLayer: LayerProps = {
            type: type,
            layerID: nextLayerID,
            orderNumber: nextOrderNumber,
            width: width,
            height: height,
            hidden: false,
            position: {
                x: Math.round((canvasWidth - width) / 2),
                y: Math.round((canvasHeight - height) / 2)
            }
        };
        
        if (type === "text") {
            baseLayer.textData = {
                fontColor: "#ffffff",
                textOpacity: 1,
                text: "New Text",
                backgroundColor: "transparent",
                backgroundOpacity: 1,
                fontSize: "16px",
                fontFamily: "Arial",
                style: [],
                borderColor: "#ffffff",
                borderOpacity: 1,
                borderWidth: 1,
                borderStyle: "none",
                autoFit: false
            };
        } else if (type === "image" || type === "ai-variation") {
            baseLayer.imageLayerStyle = {
                imageOpacity: 1,
                backgroundColor: "transparent",
                backgroundOpacity: 1,
                borderColor: "#ffffff",
                borderOpacity: 1,
                borderWidth: 1,
                borderStyle: "none"
            };
        }

        return baseLayer;
    };
    const createNewLayer = (type: LayerProps["type"]) => {
        onSelectedVariationChange?.(-1,null);
        if (type === "drawings") {
            const canvas = canvasRef?.current as HTMLCanvasElement | null;
            const canvasWidth = canvas?.width ?? 1000;
            const canvasHeight = canvas?.height ?? 800;

            const width = 240;
            const height = 180;

            const { nextLayerID, nextOrderNumber } = getNextLayerMeta(layers);        
            const newLayer: LayerProps = {
                type: "drawings",
                layerID: nextLayerID,
                orderNumber: nextOrderNumber,
                position: {
                    x: Math.round((canvasWidth - width) / 2),
                    y: Math.round((canvasHeight - height) / 2)
                },
                width,
                height,
                hidden: false,
                rotation: 0,
                flipHorizontal: false,
                flipVertical: false,
                drawingData: {
                    strokes: [],
                    rasterSrc: createTransparentRaster(width, height),
                    rasterWidth: width,
                    rasterHeight: height,
                    rotation: 0,
                    flipHorizontal: false,
                    flipVertical: false
                },
                variations: []
            };

            commitLayerChange([...layers, newLayer], [newLayer.layerID]);
            return;
        }

        const newLayer = createLayerBase(type);
        commitLayerChange([...layers, newLayer], [newLayer.layerID]);
    };
    const deleteSelectedLayers = () => {
        if (selectedLayerIDs.length === 0) return;

        const nextLayers = rebuildOrderNumbers(
            layers.filter((layer) => !selectedLayerIDs.includes(layer.layerID))
        );

        commitLayerChange(nextLayers, []);
        onSelectedLayerIDsChange?.([]);
    };
    const deleteSelectedLayer = (layerID?:number) => {
        if (!selectedLayer && layerID) return;

        const idToFilter = layerID ?? selectedLayer?.layerID;
        const nextLayers = layers.filter(
            (layer) => layer.layerID !== idToFilter
        );

        commitLayerChange(nextLayers, []);
    };
    const handleCropSelectedLayerSave = async () => {
        const selectedId =
            activeLayerEditId ?? (selectedLayerIDs.length === 1 ? selectedLayerIDs[0] : null);
        if (selectedId == null) return;

        const layer = layers.find((l) => l.layerID === selectedId);
        if (!layer) return;
        if (layer.type === "text") return;

        const crop = rectToSelectedLayerCrop(overlayRect, layer);
        if (!crop) return;

        const nextLayers = await Promise.all(
            layers.map(async (l) => {
                if (l.layerID !== selectedId) return l;

                const localCrop = {
                    x: crop.x,
                    y: crop.y,
                    w: crop.w,
                    h: crop.h
                };

                if (l.type === "image") {
                    const src = l.imageEdit?.src || l.imageData?.src;
                    if (!src) return l;

                    const croppedSrc = await cropImageLayerDataUrl(src, localCrop, l.width, l.height);
                    if (!croppedSrc) return l;

                    const imgData = l.imageEdit ?? l.imageData ?? null;
                    let imgFile = await urlToFile(croppedSrc,croppedSrc.split('/').slice(-1)[0]);
                    const nextImageBase: ImageLayerProps = {
                        ...(imgData ?? {}),
                        src: croppedSrc,
                        file: imgFile,
                        width: localCrop.w,
                        height: localCrop.h,
                        timeOfUse: new Date(),
                        rotation: imgData?.rotation ?? 0,
                        prompt_negative: imgData?.prompt_negative ?? "",
                        prompt_positive: imgData?.prompt_positive ?? "",
                        colorFilterData: imgData?.colorFilterData ?? colorFilterList[0]
                    };

                    return {
                        ...l,
                        width: localCrop.w,
                        height: localCrop.h,
                        position: {
                            x: Math.round(layerLocalPointToCanvasPoint(
                                {
                                    x: localCrop.x + localCrop.w / 2,
                                    y: localCrop.y + localCrop.h / 2
                                },
                                l
                            ).x - localCrop.w / 2),
                            y: Math.round(layerLocalPointToCanvasPoint(
                                {
                                    x: localCrop.x + localCrop.w / 2,
                                    y: localCrop.y + localCrop.h / 2
                                },
                                l
                            ).y - localCrop.h / 2)
                        },
                        cropRect: {
                            x: 0,
                            y: 0,
                            w: localCrop.w,
                            h: localCrop.h
                        },
                        imageEdit: nextImageBase,
                        imageData: nextImageBase
                    };
                }

                if (l.type === "drawings") {
                    const src = l.drawingData?.rasterSrc;
                    if (!src) return l;

                    const img = await loadImage(src);

                    const canvas = document.createElement("canvas");
                    canvas.width = localCrop.w;
                    canvas.height = localCrop.h;

                    const ctx = canvas.getContext("2d");
                    if (!ctx) return l;

                    ctx.drawImage(
                        img,
                        localCrop.x,
                        localCrop.y,
                        localCrop.w,
                        localCrop.h,
                        0,
                        0,
                        localCrop.w,
                        localCrop.h
                    );

                    return {
                        ...l,
                        width: localCrop.w,
                        height: localCrop.h,
                        cropRect: null,
                        position: {
                            x: Math.round(layerLocalPointToCanvasPoint(
                                {
                                    x: localCrop.x + localCrop.w / 2,
                                    y: localCrop.y + localCrop.h / 2
                                },
                                l
                            ).x - localCrop.w / 2),
                            y: Math.round(layerLocalPointToCanvasPoint(
                                {
                                    x: localCrop.x + localCrop.w / 2,
                                    y: localCrop.y + localCrop.h / 2
                                },
                                l
                            ).y - localCrop.h / 2)
                        },
                        drawingData: {
                            ...l.drawingData,
                            strokes: [],
                            // eslint-disable-next-line no-restricted-syntax -- internes Layer-Compositing-State (kein Download), Art. 50 KI-VO nicht einschlaegig
                            rasterSrc: canvas.toDataURL("image/png"),
                            rasterWidth: canvas.width,
                            rasterHeight: canvas.height
                        }
                    };
                }

                return l;
            })
        );

        commitLayerContentChange(nextLayers, [selectedId], [selectedId]);
        setIsClosingOverlays(true);
        setActiveLayerEditId(null);
        onToggleCropeMode?.(false);
        setLayerAreaSelection(null);
        setSelectionPreview(null);
        setMarqueeSelectionPreview(null);
    };

    const beginPointerMarqueeSelection = (e: React.MouseEvent) => {
        if (e.button !== 0) return;
        if (activeToolkitTool !== "pointer") return;
        if (cropMode || rotateMode || openExtandImage?.active) return;

        const point = clientToCanvasPoint(e.clientX, e.clientY,canvasRef);
        if (!point) return;

        const hitLayer = getTopMostLayerAtPoint(layers,point);

        if (hitLayer) {
            onSelectedLayerIDsChange?.([hitLayer.layerID]);
            onSelectedLayerChange?.(hitLayer);
            return;
        }

        onSelectedLayerIDsChange?.([]);
        onSelectedLayerChange?.(null);

        const initial = {
            startX: point.x,
            startY: point.y,
            currentX: point.x,
            currentY: point.y
        };

        marqueeSelectionRef.current = initial;
        setMarqueeSelectionPreview(initial);
    };
    const beginLayerTransform = (
        e: React.MouseEvent,
        layer: LayerProps,
        mode: "move" | "nw" | "ne" | "sw" | "se"
    ) => {
        e.preventDefault();
        e.stopPropagation();
        if (cropMode || rotateMode || openExtandImage?.active) return;
        if (layer.hidden) return;

        const isMove = mode === "move";
        const isResize = mode !== "move";

        if (isMove && activeToolkitTool !== "hand") return;
        // if (isResize && activeToolkitTool !== "pointer") return;

        //avoid hidden layer
        const existingSelection =
            selectedLayerIDs.includes(layer.layerID) && selectedLayerIDs.length > 0
                ? selectedLayerIDs.filter((id) =>
                    layers.some((l) => l.layerID === id && !l.hidden)
                )
                : [layer.layerID];

        const snapshotLayers = layers
            .filter((l) => existingSelection.includes(l.layerID))
            .map(cloneLayer);

        onSelectedLayerIDsChange?.(existingSelection);
        onSelectedLayerChange?.(layer);

        layerDragRef.current = {
            primaryLayerId: layer.layerID,
            selectedIds: existingSelection,
            mode,
            startX: e.clientX,
            startY: e.clientY,
            startLayers: snapshotLayers
        };
    };
    const deleteSelectedAreaFromLayer = async (nextSelectedIds?: number[]) : Promise<AreaOperationResult | null>  => {
        const safeNextSelectedIds = Array.isArray(nextSelectedIds) ? nextSelectedIds : undefined;

        const activeLayer = getSingleSelectedLayer();
        if (!layerAreaSelection) return null;

        const isImageSelection = layerAreaSelection.layerId === -1;
        const activeLayerForLayerSelection = !isImageSelection ? activeLayer : null;

        if (!isImageSelection) {
            if (!activeLayerForLayerSelection) return null;
            if (layerAreaSelection.layerId !== activeLayerForLayerSelection.layerID) return null;
            if (activeLayerForLayerSelection.type === "text") return null;
        }

        const canvas = canvasRef?.current as HTMLCanvasElement | null;
        if (!canvas) return null;

        const src = isImageSelection
            ? image?.src ?? null
            : getLayerRasterSource(activeLayerForLayerSelection!);

        if (!src) return null;

        const img = await loadImage(src);

        const workCanvas = document.createElement("canvas");
        workCanvas.width = isImageSelection
            ? canvas.width
            : activeLayerForLayerSelection!.width;
        workCanvas.height = isImageSelection
            ? canvas.height
            : activeLayerForLayerSelection!.height;

        const ctx = workCanvas.getContext("2d");
        if (!ctx) return null;

        ctx.drawImage(
            img,
            0,
            0,
            isImageSelection ? workCanvas.width : activeLayerForLayerSelection!.width,
            isImageSelection ? workCanvas.height : activeLayerForLayerSelection!.height
        );

        const selectionPoints: Point[] = isImageSelection
            ? (layerAreaSelection.points?.length
                ? layerAreaSelection.points
                : buildSelectionBoundaryPoints(
                    layerAreaSelection.mode,
                    layerAreaSelection.x,
                    layerAreaSelection.y,
                    layerAreaSelection.w,
                    layerAreaSelection.h
                ))
            : getLocalSelectionPointsForLayer(
                layerAreaSelection,
                activeLayerForLayerSelection!
            );

        ctx.save();
        drawPolygonPath(ctx, selectionPoints);
        ctx.clip();
        ctx.clearRect(0, 0, workCanvas.width, workCanvas.height);
        ctx.restore();

        const nextFile = await canvasToFile(
            workCanvas,
            `${isImageSelection ? "image" : activeLayerForLayerSelection!.layerID}-edited.png`
        );
        const nextSrc = URL.createObjectURL(nextFile);

        if (isImageSelection) {
            const nextImage: ImageProps = {
                file: nextFile,
                src: nextSrc,
                width: workCanvas.width,
                height: workCanvas.height,
                timeOfUse: new Date()
            };
            onBaseImageReplace?.(nextImage);
            setLayerAreaSelection(null);
            setSelectionPreview(null);

            return {
                sourceType: "base",
                updatedImage: nextImage
            };
        }
        const nextLayers = layers.map((layer) => {
            if (layer.layerID !== activeLayerForLayerSelection!.layerID) return layer;

            if (layer.type === "drawings") {
                return {
                    ...layer,
                    drawingData: {
                        ...layer.drawingData,
                        strokes: [],
                        // eslint-disable-next-line no-restricted-syntax -- internes Layer-Compositing-State (kein Download), Art. 50 KI-VO nicht einschlaegig
                        rasterSrc: workCanvas.toDataURL("image/png"),
                        rasterWidth: workCanvas.width,
                        rasterHeight: workCanvas.height
                    }
                };
            }

            return {
                ...layer,
                imageEdit: {
                    ...(layer.imageEdit ?? layer.imageData!),
                    file: nextFile,
                    src: nextSrc,
                    width: activeLayerForLayerSelection!.width,
                    height: activeLayerForLayerSelection!.height
                },
                imageData: {
                    ...(layer.imageData ?? layer.imageEdit!),
                    file: nextFile,
                    src: nextSrc,
                    width: activeLayerForLayerSelection!.width,
                    height: activeLayerForLayerSelection!.height
                }
            };
        });

        const updatedLayer = nextLayers.find(
            layer => layer.layerID === activeLayerForLayerSelection!.layerID
        ) ?? null;

        commitLayerContentChange(nextLayers, [activeLayerForLayerSelection!.layerID], nextSelectedIds);
        setLayerAreaSelection(null);
        setSelectionPreview(null);

        return {
            sourceType: "layer",
            sourceLayerId: activeLayerForLayerSelection!.layerID,
            updatedLayer: updatedLayer ?? undefined,
            nextLayers
        };
    };
    const buildCopiedAreaLayer = async (): Promise<LayerProps | null> => {
        const activeLayer = getSingleSelectedLayer();
        if (!layerAreaSelection) return null;

        const isImageSelection = layerAreaSelection.layerId === -1;
        const activeLayerForLayerSelection = !isImageSelection ? activeLayer : null;

        if (!isImageSelection) {
            if (!activeLayerForLayerSelection) return null;
            if (layerAreaSelection.layerId !== activeLayerForLayerSelection.layerID) return null;
            if (activeLayerForLayerSelection.type === "text") return null;
        }

        const selectionRect = getCanvasSelectionRect(layerAreaSelection);

        const { nextLayerID, nextOrderNumber } = getNextLayerMeta(layers);

        if (!isImageSelection && activeLayerForLayerSelection?.type === "drawings") {
            const sourceCanvas = await loadDrawingLayerCanvas(activeLayerForLayerSelection);

            const outCanvas = document.createElement("canvas");
            outCanvas.width = selectionRect.w;
            outCanvas.height = selectionRect.h;

            const octx = outCanvas.getContext("2d");
            if (!octx) return null;

            const localSelectionRect = getLocalSelectionRectForLayer(
                layerAreaSelection,
                activeLayerForLayerSelection
            );
            if (
                localSelectionRect.w <= 0 ||
                localSelectionRect.h <= 0 ||
                selectionRect.w <= 0 ||
                selectionRect.h <= 0
            ) {
                return null;
            }

           const relativePoints: Point[] = getLocalSelectionPointsForLayer(
                layerAreaSelection,
                activeLayerForLayerSelection
            ).map((p) => ({
                x: ((p.x - localSelectionRect.x) / localSelectionRect.w) * selectionRect.w,
                y: ((p.y - localSelectionRect.y) / localSelectionRect.h) * selectionRect.h
            }));

            octx.save();

            drawPolygonPath(octx, relativePoints);
            octx.clip();

            octx.drawImage(
                sourceCanvas,
                localSelectionRect.x,
                localSelectionRect.y,
                localSelectionRect.w,
                localSelectionRect.h,
                0,
                0,
                selectionRect.w,
                selectionRect.h
            );

            octx.restore();

            const nextFile = await canvasToFile(
                outCanvas,
                `${activeLayerForLayerSelection.layerID}-drawing-selection.png`
            );

            const nextSrc = URL.createObjectURL(nextFile);

            return {
                type: "image",
                layerID: nextLayerID,
                orderNumber: nextOrderNumber,
                width: selectionRect.w,
                height: selectionRect.h,
                position: {
                    x: selectionRect.x,
                    y: selectionRect.y
                },
                hidden: false,
                rotation: 0,
                flipHorizontal: false,
                flipVertical: false,
                imageData: {
                    file: nextFile,
                    src: nextSrc,
                    width: selectionRect.w,
                    height: selectionRect.h,
                    timeOfUse: new Date(),
                    title: `${activeLayerForLayerSelection.layerID}-drawing-selection`,
                    rotation: 0,
                    prompt_positive: "",
                    prompt_negative: "",
                    colorFilterData: colorFilterList[0]
                },
                imageEdit: {
                    file: nextFile,
                    src: nextSrc,
                    width: selectionRect.w,
                    height: selectionRect.h,
                    timeOfUse: new Date(),
                    title: `${activeLayerForLayerSelection.layerID}-drawing-selection`,
                    rotation: 0,
                    prompt_positive: "",
                    prompt_negative: "",
                    colorFilterData: colorFilterList[0]
                }
                };
        }

        let sourceCanvas: HTMLCanvasElement | null = null;

        if (isImageSelection) {
            const src = image?.src ?? null;
            if (!src) return null;

            const img = await loadImage(src);
            sourceCanvas = document.createElement("canvas");
            sourceCanvas.width = img.naturalWidth || img.width;
            sourceCanvas.height = img.naturalHeight || img.height;

            const sctx = sourceCanvas.getContext("2d");
            if (!sctx) return null;

            sctx.drawImage(img, 0, 0, sourceCanvas.width, sourceCanvas.height);
        } else {
            sourceCanvas = await renderSingleLayerToCanvasSpace(
                activeLayerForLayerSelection!
            );
            if (!sourceCanvas) return null;
        }

        const outCanvas = document.createElement("canvas");
        outCanvas.width = selectionRect.w;
        outCanvas.height = selectionRect.h;

        const octx = outCanvas.getContext("2d");
        if (!octx) return null;

        const relativePoints: Point[] = (
            layerAreaSelection.points?.length
                ? layerAreaSelection.points
                : buildSelectionBoundaryPoints(
                    layerAreaSelection.mode,
                    layerAreaSelection.x,
                    layerAreaSelection.y,
                    layerAreaSelection.w,
                    layerAreaSelection.h
                )
        ).map((p) => ({
            x: p.x - selectionRect.x,
            y: p.y - selectionRect.y
        }));

        octx.save();
        drawPolygonPath(octx, relativePoints);
        octx.clip();

        octx.drawImage(
            sourceCanvas,
            selectionRect.x,
            selectionRect.y,
            selectionRect.w,
            selectionRect.h,
            0,
            0,
            selectionRect.w,
            selectionRect.h
        );

        octx.restore();

        const nextFile = await canvasToFile(
            outCanvas,
            `${isImageSelection ? "image" : activeLayerForLayerSelection!.layerID}-copy.png`
        );
        const nextSrc = URL.createObjectURL(nextFile);

        return {
            type: "image",
            layerID: nextLayerID,
            orderNumber: nextOrderNumber,
            width: selectionRect.w,
            height: selectionRect.h,
            position: {
                x: selectionRect.x,
                y: selectionRect.y
            },
            hidden: false,
            imageData: {
                file: nextFile,
                src: nextSrc,
                width: selectionRect.w,
                height: selectionRect.h,
                timeOfUse: new Date(),
                title: `${isImageSelection ? "image" : activeLayerForLayerSelection!.layerID}-copy`,
                rotation: 0,
                prompt_positive: "",
                prompt_negative: "",
                colorFilterData: colorFilterList[0]
            },
            imageEdit: {
                file: nextFile,
                src: nextSrc,
                width: selectionRect.w,
                height: selectionRect.h,
                timeOfUse: new Date(),
                rotation: 0,
                prompt_negative: "",
                prompt_positive: "",
                colorFilterData: colorFilterList[0]
            }
        };
    };
    const buildAiReferenceLayerFromSelection = async (): Promise<LayerProps | null> => {
        return await buildCopiedAreaLayer();
    };
    const buildAiReferenceLayerFromLayer = async (
        layer: LayerProps
    ): Promise<LayerProps | null> => {
        if (layer.type === "image" || layer.type === "ai-variation") {
            return layer;
        }

        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(layer.width));
        canvas.height = Math.max(1, Math.round(layer.height));

        const ctx = canvas.getContext("2d");
        if (!ctx) return null;

        await renderLayerToCanvas(ctx, {
            ...layer,
            position: { x: 0, y: 0 },
            rotation: 0
        });

        const file = await canvasToFile(canvas, `layer-${layer.layerID}.png`);
        const src = URL.createObjectURL(file);

        return {
            ...layer,
            type: "image",
            imageData: {
            file,
            src,
            width: canvas.width,
            height: canvas.height,
            timeOfUse: new Date(),
            title: `Layer ${layer.layerID}`
            },
            imageEdit: {
            file,
            src,
            width: canvas.width,
            height: canvas.height,
            timeOfUse: new Date(),
            title: `Layer ${layer.layerID}`
            }
        };
    };
    const copySelectedAreaFromLayer = async (): Promise<AreaOperationResult | null> => {
        const copiedLayer = await buildCopiedAreaLayer();
        if (!copiedLayer) return null;

        const isImageSelection = layerAreaSelection?.layerId === -1;
        const nextLayers = [...layers, copiedLayer];

        commitLayerChange(nextLayers, [copiedLayer.layerID]);
        setLayerAreaSelection(null);
        setSelectionPreview(null);

        return {
            sourceType: isImageSelection ? "base" : "layer",
            sourceLayerId: isImageSelection ? undefined : layerAreaSelection?.layerId ?? undefined,
            copiedLayer,
            nextLayers
        };
    };
    const extractSelectedAreaToNewLayer = async (): Promise<AreaOperationResult | null> => {
        if (!layerAreaSelection) return null;

        const copiedLayer = await buildCopiedAreaLayer();
        if (!copiedLayer) return null;

        const activeLayer = getSingleSelectedLayer();
        const isImageSelection = layerAreaSelection.layerId === -1;
        const activeLayerForLayerSelection = !isImageSelection ? activeLayer : null;

        if (!isImageSelection) {
            if (!activeLayerForLayerSelection) return null;
            if (layerAreaSelection.layerId !== activeLayerForLayerSelection.layerID) return null;
            if (activeLayerForLayerSelection.type === "text") return null;
        }

        const src = isImageSelection
            ? image?.src ?? null
            : getLayerRasterSource(activeLayerForLayerSelection!);
        if (!src) return null;

        const img = await loadImage(src);

        const workCanvas = document.createElement("canvas");
        workCanvas.width = isImageSelection
            ? img.naturalWidth || img.width
            : activeLayerForLayerSelection!.width;
        workCanvas.height = isImageSelection
            ? img.naturalHeight || img.height
            : activeLayerForLayerSelection!.height;

        const ctx = workCanvas.getContext("2d");
        if (!ctx) return null;

        ctx.drawImage(img, 0, 0, workCanvas.width, workCanvas.height);

                const selectionPoints: Point[] = isImageSelection
            ? (layerAreaSelection.points?.length
                ? layerAreaSelection.points
                : buildSelectionBoundaryPoints(
                    layerAreaSelection.mode,
                    layerAreaSelection.x,
                    layerAreaSelection.y,
                    layerAreaSelection.w,
                    layerAreaSelection.h
                ))
            : getLocalSelectionPointsForLayer(
                layerAreaSelection,
                activeLayerForLayerSelection!
            );

        ctx.save();
        drawPolygonPath(ctx, selectionPoints);
        ctx.clip();
        ctx.clearRect(0, 0, workCanvas.width, workCanvas.height);
        ctx.restore();

        const nextFile = await canvasToFile(
            workCanvas,
            `${isImageSelection ? "image" : activeLayerForLayerSelection!.layerID}-edited.png`
        );
        const nextSrc = URL.createObjectURL(nextFile);

        if (isImageSelection) {
            const nextImage: ImageProps = {
                file: nextFile,
                src: nextSrc,
                width: workCanvas.width,
                height: workCanvas.height,
                timeOfUse: new Date()
            };
            const nextLayers = [...layers, copiedLayer];

            onBaseImageReplace?.(nextImage);
            commitLayerChange(nextLayers, [copiedLayer.layerID]);
            setLayerAreaSelection(null);
            setSelectionPreview(null);

            return {
                sourceType: "base",
                copiedLayer,
                updatedImage: nextImage,
                nextLayers
            };
        }

        const deletedLayers = layers.map((layer) => {
            if (layer.layerID !== activeLayerForLayerSelection!.layerID) return layer;

            if (layer.type === "drawings") {
                return {
                    ...layer,
                    drawingData: {
                        ...layer.drawingData,
                        strokes: [],
                        // eslint-disable-next-line no-restricted-syntax -- internes Layer-Compositing-State (kein Download), Art. 50 KI-VO nicht einschlaegig
                        rasterSrc: workCanvas.toDataURL("image/png"),
                        rasterWidth: workCanvas.width,
                        rasterHeight: workCanvas.height
                    }
                };
            }

            return {
                ...layer,
                imageEdit: {
                    ...(layer.imageEdit ?? layer.imageData!),
                    file: nextFile,
                    src: nextSrc,
                    width: activeLayerForLayerSelection!.width,
                    height: activeLayerForLayerSelection!.height
                },
                imageData: {
                    ...(layer.imageData ?? layer.imageEdit!),
                    file: nextFile,
                    src: nextSrc,
                    width: activeLayerForLayerSelection!.width,
                    height: activeLayerForLayerSelection!.height
                }
            };
        });

        const nextLayers = [...deletedLayers, copiedLayer];

        const updatedLayer = deletedLayers.find(
            layer => layer.layerID === activeLayerForLayerSelection!.layerID
        ) ?? null;

        commitLayerChange(nextLayers, [copiedLayer.layerID]);
        setLayerAreaSelection(null);
        setSelectionPreview(null);

        return {
            sourceType: "layer",
            sourceLayerId: activeLayerForLayerSelection!.layerID,
            copiedLayer,
            updatedLayer: updatedLayer ?? undefined,
            nextLayers
        };
    };
    const duplicateSelectedLayer = (layerID?:number) => {
        if (selectedLayerIDs.length === 0 && !layerID) return;
        let layersToDuplicate: LayerProps[] = [];
        if(layerID){
            layersToDuplicate = layers.filter((layer) =>
                layerID === layer.layerID
            );
        }
        else{
            layersToDuplicate = layers.filter((layer) =>
                selectedLayerIDs.includes(layer.layerID)
            );
        }

        let nextLayers = [...layers];
        const newIds: number[] = [];

        for (const layer of layersToDuplicate) {
            const { nextLayerID, nextOrderNumber } = getNextLayerMeta(nextLayers);

            const duplicatedLayer: LayerProps = {
                ...cloneLayer(layer),
                layerID: nextLayerID,
                orderNumber: nextOrderNumber,
                position: {
                    x: layer.position.x + 20,
                    y: layer.position.y + 20
                }
            };

            nextLayers.push(duplicatedLayer);
            newIds.push(duplicatedLayer.layerID);
        }

        commitLayerChange(nextLayers, newIds);
    };

    const mergeLayersInternal = async (layersToMerge: LayerProps[]) => {
        const visibleLayersToMerge = layersToMerge.filter(layer => !layer.hidden);

        if (visibleLayersToMerge.length < 2) return;

        const [mergedLayer, mergedIds] = await mergeLayerAndGetLayerWithSelectionIds(visibleLayersToMerge);
        if (!mergedLayer) return;

        const remainingLayers = layers.filter(layer => !mergedIds.has(layer.layerID));

        const nextLayers = [...remainingLayers, mergedLayer];

        const normalized = rebuildOrderNumbers(
            sortLayersByOrderAscending(nextLayers)
        );

        commitLayerChange(normalized, [mergedLayer.layerID]);
        setLayerAreaSelection(null);
        setSelectionPreview(null);
    };

    const mergeSelectedLayerWithPrevious = async (layerId?:number) => {
        if (selectedLayerIDs.length !== 1 && !layerId) return;

        const idToFind = layerId ?? selectedLayerIDs[0];
        const currentLayer = layers.find((l) => l.layerID === idToFind);
        if (!currentLayer) return;

        const currentOrder = Number(currentLayer.orderNumber);
        if (currentLayer.hidden) return;
        const previousLayer = layers
            .filter((l) =>
                !l.hidden &&
                Number(l.orderNumber) < currentOrder
            )
            .sort((a, b) => Number(b.orderNumber) - Number(a.orderNumber))[0];

        if (!previousLayer) return;

        await mergeLayersInternal([previousLayer, currentLayer]);
    };

    const mergeAllSelectedLayers = async () => {
        if (selectedLayerIDs.length < 2) return;

        const selectedLayers = layers.filter((l) =>
            selectedLayerIDs.includes(l.layerID) &&
            !l.hidden
        );

        if (selectedLayers.length < 2) return;

        await mergeLayersInternal(selectedLayers);
    };
    const pasteClipboardContent = async () => {
        const clipboard = clipboardRef.current;
        if (!clipboard) return;

        if (clipboard.type === "area") {
            await copySelectedAreaFromLayer();
            return;
        }

        if (clipboard.type === "layers") {
            let nextLayers = [...layers];
            const newIds: number[] = [];

            for (const layer of clipboard.layers) {
                const { nextLayerID, nextOrderNumber } = getNextLayerMeta(nextLayers);

                const duplicatedLayer: LayerProps = {
                    ...cloneLayer(layer),
                    layerID: nextLayerID,
                    orderNumber: nextOrderNumber,
                    position: {
                        x: layer.position.x + 20,
                        y: layer.position.y + 20
                    }
                };

                nextLayers.push(duplicatedLayer);
                newIds.push(duplicatedLayer.layerID);
            }

            commitLayerChange(nextLayers, newIds);
        }
    };
    const beginSelectionDrag = (e: React.MouseEvent, targetLayer?: LayerProps | null) => {
        e.preventDefault();
        e.stopPropagation();

        if(!activeToolkitTool) return;
        if (!["select-rect", "select-oval", "select-free"].includes(activeToolkitTool)) return;

        const activeLayer = targetLayer ?? getSingleSelectedLayer();

        if (activeLayer?.type === "text") return;

        const point = clientToCanvasPoint(e.clientX, e.clientY, canvasRef);
        if (!point) return;

        const clamped = activeLayer
            ? clampPointToLayer(point, activeLayer)
            : point;

        const mode: LayerAreaSelectionMode =
            activeToolkitTool === "select-rect"
                ? "rect"
                : activeToolkitTool === "select-oval"
                    ? "oval"
                    : "free";

        const initial = {
            mode,
            startX: clamped.x,
            startY: clamped.y,
            currentX: clamped.x,
            currentY: clamped.y,
            points: [{ x: clamped.x, y: clamped.y }]
        };

        selectionDragRef.current = initial;
        setSelectionPreview(initial);

        if (activeLayer) {
            onSelectedLayerIDsChange?.([activeLayer.layerID]);
            onSelectedLayerChange?.(activeLayer);
        }
    };
    const beginDrawingStroke = (e: React.MouseEvent) => {
        if (!selectedDrawingTool) return;

        const layer = getSingleSelectedDrawingLayer();
        if (!layer) return;

        const canvasPoint = clientToCanvasPoint(e.clientX, e.clientY, canvasRef);
        if (!canvasPoint) return;

        const clampedCanvas = clampPointToTransformedLayer(canvasPoint, layer);
        const localPoint = canvasPointToLayerLocalPoint(clampedCanvas, layer);

        if (selectedDrawingTool === "fill") {
            void addDrawingStrokeToLayer(layer.layerID, {
                points: [{ x: localPoint.x, y: localPoint.y }],
                color: selectedDrawingColor,
                opacity: selectedBrushOpacity / 100,
                tool: "fill",
                size: 1,
                fillStart: { x: Math.round(localPoint.x), y: Math.round(localPoint.y) }
            });
            return;
        }

        drawingStrokeRef.current = {
            layerID: layer.layerID,
            tool: selectedDrawingTool,
            color: selectedDrawingTool === "eraser" ? "#000000" : selectedDrawingColor,
            size: selectedBrushSize,
            opacity: selectedBrushOpacity / 100,
            hardness: selectedBrushHardness / 100,
            points: [{ x: localPoint.x, y: localPoint.y }],
            startPoint: { x: localPoint.x, y: localPoint.y },
            currentPoint: { x: localPoint.x, y: localPoint.y }
        };
        if (!isShapeTool(selectedDrawingTool) && selectedDrawingTool !== "fill") {
            setDrawingStrokePreview({
                points: [{ x: localPoint.x, y: localPoint.y }],
                color: selectedDrawingTool === "eraser" ? "#000000" : selectedDrawingColor,
                size: selectedBrushSize,
                opacity: selectedBrushOpacity / 100,
                tool: selectedDrawingTool,
                hardness: selectedBrushHardness / 100
            });
        }

        window.addEventListener("mousemove", onDrawingStrokeMove);
        window.addEventListener("mouseup", endDrawingStroke);
    };
    const onDrawingStrokeMove = (e: MouseEvent) => {
        const active = drawingStrokeRef.current;
        if (!active) return;

        const layer = layers.find((l) => l.layerID === active.layerID);
        if (!layer || layer.type !== "drawings") return;

        const canvasPoint = clientToCanvasPoint(e.clientX, e.clientY, canvasRef);
        if (!canvasPoint) return;

        const clampedCanvas = clampPointToTransformedLayer(canvasPoint, layer);
        const localPoint = canvasPointToLayerLocalPoint(clampedCanvas, layer);

        active.currentPoint = { x: localPoint.x, y: localPoint.y };

        if (isShapeTool(active.tool)) {
            setShapePreview({
                points: getPreviewShapePoints(
                    active.tool,
                    active.startPoint,
                    active.currentPoint
                ),
                color: active.color,
                size: active.size,
                opacity: active.opacity
            });
            return;
        }

        setShapePreview(null);
        active.points.push({ x: localPoint.x, y: localPoint.y });
        setDrawingStrokePreview({
            points: [...active.points, { x: localPoint.x, y: localPoint.y }],
            color: active.color,
            size: active.size,
            opacity: active.opacity,
            tool: active.tool,
            hardness: active.hardness
        });
    };
    const endDrawingStroke = () => {
        const active = drawingStrokeRef.current;
        if (!active) return;

        let nextStroke: any = {
            points: active.points,
            color: active.color,
            size: active.size,
            opacity: active.opacity,
            hardness: active.hardness,
            tool: active.tool
        };

        if (isShapeTool(active.tool)) {
            nextStroke = {
                ...nextStroke,
                points: getPreviewShapePoints(
                    active.tool,
                    active.startPoint,
                    active.currentPoint
                ),
                filled: false
            };
        }

        void addDrawingStrokeToLayer(active.layerID, nextStroke);

        drawingStrokeRef.current = null;
        setShapePreview(null);
        setDrawingCursorPreview(null);
        setDrawingStrokePreview(null);
        window.removeEventListener("mousemove", onDrawingStrokeMove);
        window.removeEventListener("mouseup", endDrawingStroke);
    };
    const flipSelectedLayer = async (direction: "horizontal" | "vertical", layerID?:number) => {
        if (selectedLayerIDs.length !== 1 && !layerID) return;
        const selectedId = layerID ?? selectedLayerIDs[0];
        const nextLayers = layers.map((layer) => {
            if (layer.layerID !== selectedId) return layer;

            if (direction === "horizontal") {
                return {
                    ...layer,
                    flipHorizontal: !layer.flipHorizontal
                };
            }
            return {
                ...layer,
                flipVertical: !layer.flipVertical
            };
        });

        commitLayerContentChange(nextLayers, [selectedId], [selectedId]);
    };
    const rotateSelectedLayer = async (degrees: number, layerID?: number) => {
        const selectedId = layerID ??
            activeLayerEditId ?? (selectedLayerIDs.length === 1 ? selectedLayerIDs[0] : null);

        if (selectedId == null) return;

        const nextLayers = layers.map((layer) => {
            if (layer.layerID !== selectedId) return layer;

            return {
                ...layer,
                rotation: degrees
            };
        });

        commitLayerContentChange(nextLayers, [selectedId], [selectedId]);
        setActiveLayerEditId(null);
        onToggleRotationMode?.(false);
        onIsRotating?.(false);
        setIsClosingOverlays(true);
    };
    const clearAllSelections = () => {
        setLayerAreaSelection(null);
        setSelectionPreview(null);
        setMarqueeSelectionPreview(null);
        selectionDragRef.current = null;
        marqueeSelectionRef.current = null;
        onSelectedLayerIDsChange?.([]);
        onSelectedLayerChange?.(null);
    };

    //useEffects
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const activeEl = document.activeElement as HTMLElement | null;
            const tag = activeEl?.tagName?.toLowerCase();
            const isTyping =
                tag === "input" ||
                tag === "textarea" ||
                activeEl?.isContentEditable;

            if (isTyping) return;

            if (e.key === "Escape") {
                e.preventDefault();

                clearAllSelections();
                setActiveLayerEditId(null);
                onToggleCropeMode?.(false);
                onToggleRotationMode?.(false);
                onIsRotating?.(false);
                setIsClosingOverlays(true);

                return;
            }

            const hasSelectedArea = !!layerAreaSelection;
            const hasSelectedLayers = selectedLayerIDs.length > 0;

            const arrowMoveMap: Record<string, { x: number; y: number }> = {
                ArrowUp: { x: 0, y: -1 },
                ArrowDown: { x: 0, y: 1 },
                ArrowLeft: { x: -1, y: 0 },
                ArrowRight: { x: 1, y: 0 }
            };

            if (e.key in arrowMoveMap) {
                if (cropMode || rotateMode || openExtandImage?.active) return;
                if (e.ctrlKey || e.metaKey || e.altKey) return;
                if (!hasSelectedLayers) return;

                e.preventDefault();

                const direction = arrowMoveMap[e.key];
                const step = e.shiftKey ? 10 : 1;

                const nextLayers = layers.map((layer) => {
                    if (!selectedLayerIDs.includes(layer.layerID)) return layer;

                    return {
                        ...layer,
                        position: {
                            x: layer.position.x + direction.x * step,
                            y: layer.position.y + direction.y * step
                        }
                    };
                });

                commitLayerChange(nextLayers, selectedLayerIDs);
                return;
            }

            if ((e.key === "Delete" || e.key === "Backspace") && e.shiftKey) {
                if (!hasSelectedArea) return;
                e.preventDefault();
                void extractSelectedAreaToNewLayer();
                return;
            }

            if (e.key === "Delete" || e.key === "Backspace") {
                if (!hasSelectedLayers) return;
                e.preventDefault();
                deleteSelectedLayers();
                return;
            }

            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "d") {
                if (!hasSelectedLayers) return;
                e.preventDefault();
                duplicateSelectedLayer();
                return;
            }

            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "c") {
                e.preventDefault();

                if (hasSelectedArea) {
                    clipboardRef.current = { type: "area" };
                    return;
                }

                if (hasSelectedLayers) {
                    const layersToCopy = layers
                        .filter((layer) => selectedLayerIDs.includes(layer.layerID))
                        .map(cloneLayer);

                    clipboardRef.current = {
                        type: "layers",
                        layers: layersToCopy
                    };
                }
                return;
            }

            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "v") {
                if (!clipboardRef.current) return;
                e.preventDefault();
                void pasteClipboardContent();
            }
        };

        window.addEventListener("keydown", onKey);

        return () => {
            window.removeEventListener("keydown", onKey);
        };
    }, [selectedLayerIDs, selectedLayer, layerAreaSelection, layers]);
    useEffect(() => {
        const selectedVariation = getSelectedVariationImage();
        previewSelectedLayerVariation(selectedVariation);
    }, [selectedLayer?.layerID,selectedVariationIndex]);
    useEffect(() => {
        //reset selection preview and hover preview
        if (
            selectedVariationIndex !== null &&
            selectedVariationIndex >= 0
        ) {
            return;
        }

        setLayers(prevLayers =>
            prevLayers.map(layer => {
                if (layer.type === "drawings" || layer.type === "text") {
                    return {
                        ...layer,
                        imageData: undefined
                    };
                }

                return {
                    ...layer,
                    imageEdit: layer.imageData ?? undefined
                };
            })
        );
    }, [selectedLayer?.layerID, selectedLayerIDs.join(","), selectedVariationIndex]);
    const lastExternalImageSrcRef = useRef<string>("");
    useEffect(() => {
        const nextSrc = image?.src ?? "";
        if (!nextSrc) return;

        const isFirstLoad = lastExternalImageSrcRef.current === "";
        const isSameImage = lastExternalImageSrcRef.current === nextSrc;

        if (isFirstLoad) {
            lastExternalImageSrcRef.current = nextSrc;
            onSelectedLayerIDsChange?.([]);
            onSelectedLayerChange?.(null);
            setLayerAreaSelection(null);
            setSelectionPreview(null);
            setMarqueeSelectionPreview(null);
            return;
        }

        if (isSameImage) return;

        lastExternalImageSrcRef.current = nextSrc;

        if (imageChangeSource === "edit" || baseImageReplaceByVariation) {
            const keepAiExpandBaseSelection =
                imageChangeSource === "edit" &&
                openExtandImage?.mode === "variations" &&
                layerAreaSelection?.layerId === -1;

            if (!keepAiExpandBaseSelection) {
                setLayerAreaSelection(null);
            }

            setSelectionPreview(null);
            setMarqueeSelectionPreview(null);
            setBaseImageReplaceByVariation(false);
            return;
        }

        onSelectedLayerIDsChange?.([]);
        onSelectedLayerChange?.(null);
        setLayerAreaSelection(null);
        setSelectionPreview(null);
        setMarqueeSelectionPreview(null);
    }, [
        image?.src,
        imageChangeSource,
        baseImageReplaceByVariation,
        openExtandImage?.mode,
        layerAreaSelection?.layerId
    ]);
    useEffect(() => {
        const el = canvasWrapperRef?.current;
        if (!el) return;

        const stage = el.querySelector('.image-stage') as HTMLDivElement | null;
        if (!stage) return;

        if (rotateMode && overlayTarget === "image") {
            stage.style.transform = `rotate(${rotationDraft}deg)`;
            stage.style.transformOrigin = "center center";
        } else {
            stage.style.transform = "none";
            stage.style.transformOrigin = "center center";
        }

        return () => {
            stage.style.transform = 'none';
            stage.style.transformOrigin = 'center center';
        };
    }, [rotateMode, rotationDraft,overlayTarget, canvasWrapperRef]);

    useEffect(() => {
        onRotatePreviewChange?.(rotateMode);

        const onMove = (e: MouseEvent) => {
            if (!isRotating) return;
            handleRotateMove?.(e);
        };

        const onUp = () => {
            stopRotate();
        };

        if (rotateMode) {
            window.addEventListener("mousemove", onMove);
            window.addEventListener("mouseup", onUp);
        }

        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };
    }, [rotateMode, isRotating, handleRotateMove, onRotatePreviewChange]);
    useEffect(() => {
        if (!toolkitAction) return;

        switch (toolkitAction.type) {
            case "create-draw-layer":
                createNewLayer("drawings");
                break;

            case "create-text-layer":
                createNewLayer("text");
                break;

            case "create-image-layer":
                createNewLayer("image");
                break;

            case "delete-selected-layers":
                deleteSelectedLayers();
                break;

            case "delete-selected-layer":
                deleteSelectedLayer();
                break;

            case "copy-selected-area":
                void copySelectedAreaFromLayer();
                break;

            case "delete-selected-area":
                void deleteSelectedAreaFromLayer();
                break;
            case "extract-selected-area":
                void extractSelectedAreaToNewLayer();
                break;
            case "crop-selected-layer": {
                if (selectedLayerIDs.length !== 1) break;

                const selectedLayer = layers.find(
                    (layer) => layer.layerID === selectedLayerIDs[0]
                );

                if (!selectedLayer || selectedLayer.type === "text") break;

                openCrop(selectedLayerIDs[0]);
                break;
            }

            case "rotate-selected-layer": {
                if (selectedLayerIDs.length !== 1) break;
                const layer = layers.find((l) => l.layerID === selectedLayerIDs[0]);
                startRotate(layer?.rotation ?? 0, selectedLayerIDs[0]);
                break;
            }

            case "flip-horizontal-layer": {
                if (selectedLayerIDs.length !== 1) break;
                void flipSelectedLayer("horizontal");
                break;
            }

            case "flip-vertical-layer": {
                if (selectedLayerIDs.length !== 1) break;
                void flipSelectedLayer("vertical");
                break;
            }
            case "merge-selected-with-previous": {
                if (selectedLayerIDs.length !== 1) break;
                void mergeSelectedLayerWithPrevious();
                break;
            }

            case "merge-all-selected-layers": {
                if (selectedLayerIDs.length < 2) break;
                void mergeAllSelectedLayers();
                break;
            }

            default:
                break;
        }
    }, [toolkitAction?.ts]);
    useEffect(() => {
        const handleMove = (e: MouseEvent) => {
            if (!layerDragRef.current || !canvasRef?.current || !canvasDisplayRect) return;

            const drag = layerDragRef.current;
            const canvas = canvasRef.current as HTMLCanvasElement;

            const pxPerScreenX = canvas.width / canvasDisplayRect.width;
            const pxPerScreenY = canvas.height / canvasDisplayRect.height;

            const dx = (e.clientX - drag.startX) * pxPerScreenX;
            const dy = (e.clientY - drag.startY) * pxPerScreenY;

            const nextLayers = layers.map((layer) => {
                if (!drag.selectedIds.includes(layer.layerID)) return layer;

                const startLayer = drag.startLayers.find((l) => l.layerID === layer.layerID);
                if (!startLayer) return layer;

                let nextLayer = { ...layer };

                if (drag.mode === "move") {
                    nextLayer.position = {
                        x: Math.round(startLayer.position.x + dx),
                        y: Math.round(startLayer.position.y + dy)
                    };
                } else {
                    // Resize nur für den primär ausgewählten Layer
                    if (layer.layerID !== drag.primaryLayerId) return layer;

                    const localDelta = canvasDeltaToLayerLocalDelta(
                        { x: dx, y: dy },
                        startLayer
                    );

                    let nextWidth = startLayer.width;
                    let nextHeight = startLayer.height;

                    if (drag.mode.includes("e")) {
                        nextWidth = Math.max(20, Math.round(startLayer.width + localDelta.x));
                    }

                    if (drag.mode.includes("s")) {
                        nextHeight = Math.max(20, Math.round(startLayer.height + localDelta.y));
                    }

                    if (drag.mode.includes("w")) {
                        nextWidth = Math.max(20, Math.round(startLayer.width - localDelta.x));
                    }

                    if (drag.mode.includes("n")) {
                        nextHeight = Math.max(20, Math.round(startLayer.height - localDelta.y));
                    }

                    const fixedLocalPointOld = {
                        x: drag.mode.includes("w") ? startLayer.width : 0,
                        y: drag.mode.includes("n") ? startLayer.height : 0
                    };

                    const fixedCanvasPoint = layerLocalPointToCanvasPoint(
                        fixedLocalPointOld,
                        startLayer
                    );

                    const fixedLocalPointNext = {
                        x: drag.mode.includes("w") ? nextWidth : 0,
                        y: drag.mode.includes("n") ? nextHeight : 0
                    };

                    const resized = {
                        ...nextLayer,
                        position: getLayerPositionKeepingLocalPoint(
                            startLayer,
                            nextWidth,
                            nextHeight,
                            fixedLocalPointNext,
                            fixedCanvasPoint
                        ),
                        width: nextWidth,
                        height: nextHeight
                    };

                    nextLayer = applyLayerResizeContentSync(startLayer, resized);
                }

                return nextLayer;
            });

            setLayers(nextLayers);
        };

        const handleUp = async () => {
            if (!layerDragRef.current) return;

            const drag = layerDragRef.current;

            if (drag.mode !== "move") {
                const nextLayers = await Promise.all(
                    layers.map(async (layer) => {
                        const startLayer = drag.startLayers.find(
                            (l) => l.layerID === layer.layerID
                        );

                        if (!startLayer || layer.type !== "drawings") return layer;

                        if (
                            startLayer.width === layer.width &&
                            startLayer.height === layer.height
                        ) {
                            return layer;
                        }

                        const src = startLayer.drawingData?.rasterSrc;
                        if (!src) return layer;

                        const img = await loadImage(src);

                        const canvas = document.createElement("canvas");
                        canvas.width = Math.max(1, Math.round(layer.width));
                        canvas.height = Math.max(1, Math.round(layer.height));

                        const ctx = canvas.getContext("2d");
                        if (!ctx) return layer;

                        ctx.imageSmoothingEnabled = true;
                        ctx.clearRect(0, 0, canvas.width, canvas.height);
                        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                        return {
                            ...layer,
                            drawingData: {
                                ...layer.drawingData,
                                strokes: [],
                                // eslint-disable-next-line no-restricted-syntax -- internes Layer-Compositing-State (kein Download), Art. 50 KI-VO nicht einschlaegig
                                rasterSrc: canvas.toDataURL("image/png"),
                                rasterWidth: canvas.width,
                                rasterHeight: canvas.height
                            }
                        };
                    })
                );

                commitLayerChange(nextLayers, drag.selectedIds);
            }

            const finalSelected =
                layers.find((layer) => layer.layerID === drag.primaryLayerId) ??
                selectedLayer ??
                null;

            onSelectedLayerChange?.(finalSelected);
            onSelectedLayerIDsChange?.(drag.selectedIds);
            layerDragRef.current = null;
        };

        window.addEventListener("mousemove", handleMove);
        window.addEventListener("mouseup", handleUp);

        return () => {
            window.removeEventListener("mousemove", handleMove);
            window.removeEventListener("mouseup", handleUp);
        };
    }, [layers, activeToolkitTool, canvasDisplayRect, selectedLayer]);
    useEffect(() => {
        const handleMove = (e: MouseEvent) => {
            if (!marqueeSelectionRef.current) return;

            const point = clientToCanvasPoint(e.clientX, e.clientY, canvasRef);
            if (!point) return;

            const next = {
                ...marqueeSelectionRef.current,
                currentX: point.x,
                currentY: point.y
            };

            marqueeSelectionRef.current = next;
            setMarqueeSelectionPreview(next);
        };

        const handleUp = () => {
            if (!marqueeSelectionRef.current) return;

            const sel = marqueeSelectionRef.current;
            const rect = normalizeRect(sel.startX, sel.startY, sel.currentX, sel.currentY);

            const selectedIds = layers
                .filter((layer) =>
                    rectsIntersect(rect, {
                        x: layer.position.x,
                        y: layer.position.y,
                        w: layer.width,
                        h: layer.height
                    })
                )
                .map((layer) => layer.layerID);

            const nextSelectedLayer =
                selectedIds.length === 1
                    ? layers.find((layer) => layer.layerID === selectedIds[0]) ?? null
                    : null;

            onSelectedLayerIDsChange?.(selectedIds);
            onSelectedLayerChange?.(nextSelectedLayer);

            marqueeSelectionRef.current = null;
            setMarqueeSelectionPreview(null);
        };

        window.addEventListener("mousemove", handleMove);
        window.addEventListener("mouseup", handleUp);

        return () => {
            window.removeEventListener("mousemove", handleMove);
            window.removeEventListener("mouseup", handleUp);
        };
    }, [layers, activeToolkitTool]);
    useEffect(() => {
        if (!isClosingOverlays) return;

        setActiveLayerEditId(null);
        setSelectionPreview(null);
        setMarqueeSelectionPreview(null);
        selectionDragRef.current = null;
        marqueeSelectionRef.current = null;
        layerDragRef.current = null;
        drawingStrokeRef.current = null;
    }, [isClosingOverlays]);
    useEffect(() => {
        const handleSelectionMove = (e: MouseEvent) => {
            if (!selectionDragRef.current) return;

            const activeLayer = getSingleSelectedLayer();
            const point = clientToCanvasPoint(e.clientX, e.clientY, canvasRef);
            if (!point) return;

            const clamped = activeLayer
                ? clampPointToLayer(point, activeLayer)
                : point;

            if (selectionDragRef.current.mode === "free") {
                const next = {
                    ...selectionDragRef.current,
                    currentX: clamped.x,
                    currentY: clamped.y,
                    points: [...selectionDragRef.current.points, clamped]
                };

                selectionDragRef.current = next;
                setSelectionPreview(next);
                return;
            }

            const next = {
                ...selectionDragRef.current,
                currentX: clamped.x,
                currentY: clamped.y
            };

            selectionDragRef.current = next;
            setSelectionPreview(next);
        };

        const handleSelectionUp = () => {
            if (!selectionDragRef.current) return;

            const activeLayer = getSingleSelectedLayer();
            const sel = selectionDragRef.current;

            if (sel.mode === "free") {
                const closedPoints = closeFreePath(sel.points);

                const xs = closedPoints.map((p) => p.x);
                const ys = closedPoints.map((p) => p.y);

                setLayerAreaSelection({
                    layerId: activeLayer?.layerID ?? -1, // -1 one if selection in base image
                    mode: "free",
                    x: Math.min(...xs),
                    y: Math.min(...ys),
                    w: Math.max(...xs) - Math.min(...xs),
                    h: Math.max(...ys) - Math.min(...ys),
                    points: closedPoints
                });
            } else {
                const x = Math.min(sel.startX, sel.currentX);
                const y = Math.min(sel.startY, sel.currentY);
                const w = Math.abs(sel.currentX - sel.startX);
                const h = Math.abs(sel.currentY - sel.startY);

                 const boundaryPoints = buildSelectionBoundaryPoints(
                    sel.mode,
                    x,
                    y,
                    w,
                    h
                );

                setLayerAreaSelection({
                    layerId: activeLayer?.layerID ?? -1,
                    mode: sel.mode,
                    x,
                    y,
                    w,
                    h,
                    points: boundaryPoints
                });
            }

            selectionDragRef.current = null;
            setSelectionPreview(null);
        };

        window.addEventListener("mousemove", handleSelectionMove);
        window.addEventListener("mouseup", handleSelectionUp);

        return () => {
            window.removeEventListener("mousemove", handleSelectionMove);
            window.removeEventListener("mouseup", handleSelectionUp);
        };
    }, [layers, activeToolkitTool, selectedLayerIDs, canvasRef]);
    useEffect(() => {
        const handleHoverMove = (e: MouseEvent) => {
            const layer = getSingleSelectedDrawingLayer();

            const isSelectionTool =
                activeToolkitTool === "select-rect" ||
                activeToolkitTool === "select-oval" ||
                activeToolkitTool === "select-free";

            const isDrawingCursorTool =
                selectedDrawingTool === "brush" ||
                selectedDrawingTool === "eraser" ||
                selectedDrawingTool === "fill";

            const shouldShowBrushPreview =
                !!layer &&
                isDrawingCursorTool &&
                activeToolkitTool !== "hand" &&
                !isSelectionTool &&
                !drawingStrokeRef.current;

            if (!shouldShowBrushPreview) {
                setDrawingCursorPreview(null);
                return;
            }

            const canvasPoint = clientToCanvasPoint(e.clientX, e.clientY, canvasRef);
            if (!canvasPoint) {
                setDrawingCursorPreview(null);
                return;
            }

            const clampedCanvas = clampPointToLayer(canvasPoint, layer);

            setDrawingCursorPreview({
                x: clampedCanvas.x,
                y: clampedCanvas.y,
                size: selectedBrushSize,
                color: selectedDrawingTool === "eraser" ? "#6c757d" : selectedDrawingColor,
                visible: true
            });
        };

        const handleLeave = () => setDrawingCursorPreview(null);

        window.addEventListener("mousemove", handleHoverMove);
        window.addEventListener("mouseup", handleLeave);

        return () => {
            window.removeEventListener("mousemove", handleHoverMove);
            window.removeEventListener("mouseup", handleLeave);
        };
    }, [
        layers,
        selectedLayerIDs,
        selectedBrushSize,
        selectedDrawingColor,
        selectedDrawingTool,
        activeToolkitTool
    ]);

    const layerSideBar = (
        <LayerSidebar
            layers={layers}
            selectedLayer={selectedLayer}
            selectedLayerIDs={selectedLayerIDs}
            onReorderLayers={handleSidebarReorder}
            onSelectedLayerChange={onSelectedLayerChange}
            onSelectedLayerIDsChange={onSelectedLayerIDsChange}
            baseImage={baseImage}
            baseImageVariations={ baseImage ? baseImageVariations : []}
            isBaseImageSelected={!selectedLayer && selectedLayerIDs.length === 0}
            onSelectBaseImage={onSelectBaseImage}
            selectedVariationIndex={selectedVariationIndex}
            onVariationHover={handleVariationHoverInternal}
            onSelectedVariationChange={handleSelectedVariationChangeInternal}
            onAddVariationAsLayer={(img, targetLayer) => addSelectionAsNewImageLayerVariation(img, targetLayer)}
            onChangeVariationAsLayer={(img, targetLayer) => replaceSelectedLayerWithVariation(img, targetLayer)}
            onSetVariationOnImage={(img, targetLayer) => addVariationToBaseImage(img, targetLayer)}
            onCancelSelection={handleOnCancelSelectionInternal}
            onReplaceBaseImage={handleBaseImageReplaceWithVariationInternal}
            setLayerHiddenState={setLayerHiddenState}
            onClearSidebarItems={clearSidebarItems}
            //contextmenus
            onDeleteSelectedLayer={(layerID:number) => deleteSelectedLayer(layerID)}
            onDuplicateSelectedLayer={(layerID:number) => duplicateSelectedLayer(layerID)}
            onRotateSelectedLayer={(degrees:number,layerID:number) => openRotate(degrees,layerID)}
            onCropSelectedLayerSave={(layerID:number) => openCrop(layerID)}
            onFlipHorizontalSelectedLayer={(layerID:number) => { void flipSelectedLayer("horizontal", layerID)}}
            onFlipVerticalSelectedLayer={(layerID:number) => { void flipSelectedLayer("vertical", layerID)}}
            onWriteSelectedLayerToBaseImage={(layerIDs:number[]) => { void handleWriteLayerOnBaseImage('selected', layerIDs)}}
            onMergeSelectedLayer={mergeAllSelectedLayers}
            onMergeLayerWithPrevious={(layerID:number) => mergeSelectedLayerWithPrevious(layerID)}
            onResetBaseImage={handleResetBase}
            onUndoBaseImage={handleUndoBase}
            onRedoBaseImage={handleRedoBase}
            onRotateBaseImage={(degrees) => openRotate(degrees)}
            onFlipVerticalBaseImage= {flipCanvasVertical}
            onFlipHorizontalBaseImage={flipCanvasHorizontal}
            onWriteAllLayerToBaseImage={() => handleWriteLayerOnBaseImage('all')}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onCropBaseImage={onCropBaseImage}
            onDeleteVariation={onDeleteVariation}
            onChangeLayerToBack={handleLayerToBack}
            onChangeLayerToFront={handleLayerToFront}
        />
    );
    return [
        layers,
        setLayers,
        overlayRect,
        layerAreaSelection,
        marqueeSelectionPreview,
        selectionPreview,
        activeOverlayBaseRect,
        activeLayerEditId,
        handleCropSelectedLayerSave,
        rotateSelectedLayer,
        updatePointerSelection,
        beginLayerTransform, 
        beginSelectionDrag,
        beginDrawingStroke, 
        beginPointerMarqueeSelection,
        setLayerAreaSelection,
        setOverlayRect,
        deleteSelectedAreaFromLayer,
        copySelectedAreaFromLayer,
        buildAiReferenceLayerFromSelection,
        buildAiReferenceLayerFromLayer,
        extractSelectedAreaToNewLayer,
        duplicateSelectedLayer,
        drawingCursorPreview,
        drawingStrokePreview,
        shapePreview,
        toggleSelectedTextStyle,
        setSelectedTextContent,
        setSelectedTextColor,
        setSelectedTextOpacity,
        setSelectedTextSize,
        setSelectedTextFontFamily,
        setSelectedTextBorderColor,
        setSelectedTextBorderOpacity,
        setSelectedTextBorderWidth,
        setSelectedTextBorderStyle,
        setSelectedTextBackgroundColor,
        setSelectedTextBackgroundOpacity,
        setSelectedImageLayerSource,
        setSelectedImageLayerOpacity,
        setSelectedImageLayerBackgroundColor,
        setSelectedImageLayerBackgroundOpacity,
        setSelectedImageLayerBorderColor,
        setSelectedImageLayerBorderOpacity,
        setSelectedImageLayerBorderWidth,
        setSelectedImageLayerBorderStyle,
        createImageLayerFromImage,
        undoSelectedLayerContentChange,
        redoSelectedLayerContentChange,
        commitLayerChange,
        extendSelectedLayerTransparent,
        handleLayerToBack,
        handleLayerToFront,
        layerSideBar
    ]

};